(function () {
  'use strict';

  let lastModelPickForBubble = null;
  let lastModelBubbleTrackCtx = null;
  let moveIndicatorTrackRaf = 0;
  let lastLoadedProductBubbleMode = 'color';
  const modelBubbleModeMap = new Map();
  const SINGLE_MODEL_KEY = 'c3d-single-main';
  const CPC_QTY_MULT = '\u00D7';
  const PDP_ADDONS_STORAGE_KEY = 'customization3d_pdp_addons';
  const C3D_SESSION_DRAFT_KEY = 'customization3d_session_draft';

  const c3dT = (key, vars) => {
    let s = window.CUSTOMIZATION_3D_STRINGS?.[key];
    if (s == null || s === '') return key;
    if (vars && typeof vars === 'object') {
      Object.keys(vars).forEach((k) => {
        const val = vars[k] == null ? '' : String(vars[k]);
        s = s.split(`{{ ${k} }}`).join(val);
      });
    }
    return s;
  };
  const C3D_SDK_URL = (typeof window.CUSTOMIZATION_3D_SDK_URL === 'string' && window.CUSTOMIZATION_3D_SDK_URL.trim())
    ? window.CUSTOMIZATION_3D_SDK_URL.trim()
    : 'https://spaces.miradoroutdoor.com/sdk/index.global.js?v=123456';
  let loadOpSeq = 0;
  let c3dSdkPreloadStarted = false;
  let hotspotsLoadedOnce = false;
  const panelProductsLoadCache = new Map();

  const get3DViewer = () => window.Customization3DViewer;

  const getVendor = () => window.Customization3DViewer?.vendor || window.Customization3DVendor;

  const isModelMoveIndicatorEnabled = () =>
    window.CUSTOMIZATION_3D_SHOW_MODEL_MOVE_INDICATOR === true;

  let pending3dPlacement = null;
  let placementZonesRevealInProgress = false;
  const C3D_MODEL_BUBBLE_GRACE_MS = 480;

  const isModelBubbleInteractionGuarded = () => {
    try {
      if (window.__C3D_PRESERVE_MODEL_BUBBLE_ON_HIDE_ALL === true) return true;
      return Date.now() < Number(window.__C3D_MODEL_BUBBLE_GRACE_UNTIL || 0);
    } catch (_) {
      return false;
    }
  };

  const markModelBubbleInteractionGrace = (ms = C3D_MODEL_BUBBLE_GRACE_MS) => {
    try {
      window.__C3D_MODEL_BUBBLE_GRACE_UNTIL = Date.now() + ms;
    } catch (_) {
    }
  };

  const touchModelBubbleInteractionGrace = (ms = 160) => {
    try {
      const until = Date.now() + ms;
      const cur = Number(window.__C3D_MODEL_BUBBLE_GRACE_UNTIL || 0);
      if (until > cur) window.__C3D_MODEL_BUBBLE_GRACE_UNTIL = until;
    } catch (_) {
    }
  };

  const clearModelBubbleInteractionGrace = () => {
    try {
      window.__C3D_MODEL_BUBBLE_GRACE_UNTIL = 0;
      window.__C3D_PRESERVE_MODEL_BUBBLE_ON_HIDE_ALL = false;
    } catch (_) {
    }
  };
  let preparePlacementMutex = Promise.resolve();
  let pending3dPlacementSeq = 0;
  let activeHotspotSkuFilter = null;
  let activePergolaSizeLegs = null;
  let activePergolaProductLine = null;

  const normalizeC3dBubbleMode = (raw) => {
    const v = String(raw || '').trim().toLowerCase();
    if (v === 'furniture') return 'rotate';
    if (v === 'primary_color') return 'primary_color';
    return 'color';
  };

  const logSkuPlacement = (stage, extra = {}) => {
    if (window.CUSTOMIZATION_3D_LOG_SKU_MODEL_CHAIN !== true) return;
    console.info('[3D][SKU->Placement]', { stage, ...extra });
  };

  const clearPending3dPlacement = () => {
    document.querySelectorAll('.product-pending-placement').forEach((el) => {
      el.classList.remove('product-pending-placement');
    });
    document.querySelectorAll('.product-selected').forEach((el) => {
      el.classList.remove('product-selected');
    });
    pending3dPlacement = null;
    getVendor()?.setActiveSku?.('');
  };

  const cancelPending3dPlacement = async () => {
    const restoreSku = String(getVendor()?.getLastCommittedAccessorySku?.() || '').trim();
    clearPending3dPlacement();
    if (restoreSku) {
      await getVendor()?.syncPlacementDivisionsForSku?.(restoreSku);
    } else {
      getVendor()?.hidePlacementZones?.();
    }
  };

  const syncStateWithRemovedModelKeys = (state, removedModelIds) => {
    if (!state || !Array.isArray(removedModelIds) || !removedModelIds.length) return;
    const removedSet = new Set(
      removedModelIds
        .map((v) => String(v || '').trim())
        .filter(Boolean)
    );
    if (!removedSet.size) return;
    const lineIds = [];
    state.get().forEach((data, lineId) => {
      const key = String(data?.modelKey || '').trim();
      if (!key || data?.group === 'single') return;
      if (removedSet.has(key)) lineIds.push(lineId);
    });
    if (!lineIds.length) return;
    lineIds.forEach((lineId) => {
      state.remove(lineId);
    });
  };

  const clearAccessorySelections = async (state, options = {}) => {
    if (!state) return false;
    const remove3dModels = options.remove3dModels !== false;
    const lineIds = [];
    const modelKeys = new Set();
    state.get().forEach((data, lineId) => {
      if (data?.group === 'single') return;
      lineIds.push(lineId);
      const key = String(data?.modelKey || lineId || '').trim();
      if (key && key !== SINGLE_MODEL_KEY) modelKeys.add(key);
    });
    if (!lineIds.length) return false;

    state.batch(() => {
      lineIds.forEach((id) => state.remove(id));
    });

    if (remove3dModels) {
      const viewer = get3DViewer();
      for (const key of modelKeys) {
        try {
          await viewer?.removeRegisteredModelByKey?.(key);
        } catch (err) {
          console.warn('[C3D] clearAccessorySelections remove model failed', { key, err });
        }
      }
    }

    hideModelActionBubble();
    removePreviewBubble();
    clearPending3dPlacement();
    getVendor()?.hidePlacementZones?.();
    persistCustomizationSessionDraft(state);
    return true;
  };

  const commitPendingPlacementToState = (state, modelId, sku = '') => {
    if (!pending3dPlacement?.wrapper || !pending3dPlacement?.variantId) return false;
    const pendingSku = String(pending3dPlacement.sku || '').trim();
    const placedSku = String(sku || pendingSku).trim();
    if (pendingSku && placedSku && pendingSku !== placedSku) return false;
    const runtimeModelId = String(modelId || '').trim();
    if (!runtimeModelId) return false;
    addProductSelectionFromWrapper(
      state,
      pending3dPlacement.wrapper,
      pending3dPlacement.variantId,
      1,
      runtimeModelId
    );
    getVendor()?.commitPlacementZoneDivisions?.(null, placedSku);
    clearPending3dPlacement();
    show3DSceneActive();
    getVendor()?.hidePlacementZones?.();
    return true;
  };

  const show3DSceneActive = () => {
    const container = document.querySelector('[data-3d-container]');
    const empty = document.querySelector('[data-3d-empty]');
    const loading = document.querySelector('[data-3d-loading]');
    if (loading) loading.style.display = 'none';
    if (empty) empty.style.display = 'none';
    if (container) container.classList.add('has-model');
  };

  const ensure3DReady = async () => {
    const viewer = get3DViewer();
    if (!viewer?.whenReady) {
      showNotification(c3dT('viewer_not_loaded'), 'warning');
      return false;
    }
    const ok = await viewer.whenReady();
    if (!ok) {
      showNotification(c3dT('viewer_start_failed'), 'warning');
    }
    return ok;
  };

  const restoreBaseSceneIfEmpty = async () => {
    try {
      const vendor = getVendor();
      if (vendor?.ensureAutoLoadBaseModels) await vendor.ensureAutoLoadBaseModels();
      else await vendor?.autoLoadBaseModels?.();
      show3DSceneActive();
    } catch (err) {
      console.warn('[C3D] restoreBaseSceneIfEmpty failed:', err);
    }
  };

  const syncPergolaSkuFromState = (state) => {
    const vendor = getVendor();
    if (!vendor?.setPergolaSku || !state) return false;
    const entry = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single');
    if (!entry) return false;
    const [, data] = entry;
    const wrapper =
      document.querySelector(
        `.product-card-clickable[data-product-id="${CSS.escape(String(data.productId))}"][data-selection-group="single"]`
      )
      || document.querySelector(
        `.product-card-clickable[data-product-id="${CSS.escape(String(data.productId))}"]`
      );
    const variant = wrapper ? getVariantById(wrapper, data.variantId) : null;
    const sku = String(variant?.sku || '').trim();
    if (!sku) return false;
    vendor.setPergolaSku(sku);
    return true;
  };

  const notifySelectPergolaFirst = () => {
    showNotification(c3dT('select_pergola_first'), 'warning');
  };

  const hasMainPergolaLoadedInScene = (state) => {
    if (!state) return false;
    const singleEntry = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single');
    if (!singleEntry) return false;

    const [, singleData] = singleEntry;
    const vendor = getVendor();
    syncPergolaSkuFromState(state);
    const pergolaSku = String(vendor?.getPergolaSku?.() || '').trim();
    if (!pergolaSku) return false;

    const modelKey = String(singleData?.modelKey || '').trim();
    if (modelKey && !/_\d{10,}$/.test(modelKey)) return true;

    const viewerAttachKey = String(get3DViewer()?.getCurrentModelAttachKey?.() || '').trim();
    if (viewerAttachKey && !/_\d{10,}$/.test(viewerAttachKey)) return true;

    const container = document.querySelector('[data-3d-container]');
    return Boolean(
      container?.classList.contains('has-model')
      && vendor?.hasSceneModels?.()
    );
  };

  const canPlaceAccessory = (state) => hasMainPergolaLoadedInScene(state);

  const preparePlacementBySku = async (wrapper, variant) => {
    const sku = variant?.sku != null ? String(variant.sku).trim() : '';
    if (!sku) {
      showNotification(c3dT('variant_no_sku'), 'warning');
      console.warn('[C3D] preparePlacementBySku: empty sku', {
        variantId: variant?.id,
        productId: wrapper?.dataset?.productId,
      });
      return false;
    }

    const selectionGroup = wrapper.dataset.selectionGroup || 'multi';
    if (selectionGroup !== 'single' && !canPlaceAccessory(getC3dState())) {
      notifySelectPergolaFirst();
      return false;
    }

    const ready = await ensure3DReady();
    if (!ready) return false;

    const vendor = getVendor();
    if (!vendor?.showPlacementZonesBySku) {
      showNotification(c3dT('vendor_not_loaded'), 'warning');
      return false;
    }

    hideModelActionBubble();
    clearPending3dPlacement();
    const placementSeq = ++pending3dPlacementSeq;
    pending3dPlacement = {
      wrapper,
      variantId: variant.id,
      variant,
      sku,
      productId: wrapper.dataset.productId,
      seq: placementSeq,
    };
    wrapper.classList.add('product-pending-placement');

    logSkuPlacement('card.click', { sku, variantId: variant.id, productId: wrapper.dataset.productId });
    vendor.setActiveSku(sku);

    if (selectionGroup !== 'single') {
      const st = getC3dState();
      if (st) syncPergolaSkuFromState(st);
    }
    let out;
    placementZonesRevealInProgress = true;
    try {
      out = await vendor.showPlacementZonesBySku(sku, {
        replaceScene: selectionGroup === 'single',
      });
    } catch (err) {
      out = { ok: false, reason: 'error', error: err };
    } finally {
      placementZonesRevealInProgress = false;
    }
    logSkuPlacement('config.result', { sku, ...out });
    if (pending3dPlacement?.seq !== placementSeq) return false;

    if (out?.ok) {
      show3DSceneActive();
      if (out.mode === 'placement_zone' && Array.isArray(out.divisions) && out.divisions.length) {
        pending3dPlacement.placementDivisions = out.divisions;
      }
      if (out.mode === 'events') {
        clearPending3dPlacement();
      }
      return out;
    }

    clearPending3dPlacement();
    const hasSceneModels = Boolean(getVendor()?.hasSceneModels?.());
    if (!hasSceneModels) await restoreBaseSceneIfEmpty();
    else show3DSceneActive();
    const reason = out?.reason || out?.mode || 'unknown';
    if (reason === 'pergola_required') {
      notifySelectPergolaFirst();
      return false;
    }
    if (reason === 'no_matching_divisions') {
      showNotification(c3dT('no_matching_divisions'), 'warning');
      return false;
    }
    if (hasSceneModels && (reason === 'no_events' || out?.mode === 'no_events' || out?.kept_scene)) {
      return { ok: true, mode: 'events', sku };
    }
    if (reason === 'dropzone_api_missing') {
      showNotification(c3dT('dropzone_api_missing'), 'warning');
      console.warn('[C3D] dropZone API missing on loaded SDK');
      return false;
    }
    const errMsg = out?.error?.message ? ` ${out.error.message}` : '';
    showNotification(
      c3dT('no_3d_config_for_sku', { sku, reason, error: errMsg }),
      'warning'
    );
    console.warn('[C3D] preparePlacementBySku failed', { sku, out });
    return false;
  };

  const queuePreparePlacementBySku = (wrapper, variant) => {
    const job = preparePlacementMutex
      .catch(() => {})
      .then(() => preparePlacementBySku(wrapper, variant));
    preparePlacementMutex = job.catch(() => {});
    return job;
  };

  const getProductBubbleModeById = (productId) => {
    if (!productId) return 'color';
    const card = document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);
    const rawCard = String(card?.dataset?.c3dBubble || '').trim();
    const rawPanel = String(card?.closest?.('.panel-content')?.dataset?.c3dPanelBubble || '').trim();
    return normalizeC3dBubbleMode(rawCard || rawPanel);
  };

  const isPrimaryBubbleMode = (mode) => String(mode || '').toLowerCase() === 'primary_color';

  const isPrimaryProductBubbleContext = (state, rowForUnload, pickAttachKey, productId) => {
    if (rowForUnload?.group === 'single') return true;
    if (pickAttachKey === SINGLE_MODEL_KEY) return true;
    const pid = productId || rowForUnload?.productId;
    if (pid && isPrimaryBubbleMode(getProductBubbleModeById(pid))) return true;
    if (rowForUnload && isPrimaryBubbleMode(rowForUnload.bubbleMode)) return true;
    if (state && pickAttachKey) {
      const singles = Array.from(state.get().entries()).filter(([, data]) => data?.group === 'single');
      if (singles.length === 1) {
        const modelKey = String(singles[0][1]?.modelKey || '').trim();
        if (!modelKey || modelKey === pickAttachKey) return true;
        if (pickAttachKey === 'pergola' || /_pergola$/i.test(pickAttachKey)) return true;
        const isAccessoryKey = Array.from(state.get().values()).some((data) => (
          data?.group !== 'single'
          && String(data?.modelKey || '').trim() === pickAttachKey
        ));
        if (!isAccessoryKey && !/_\d{10,}$/.test(pickAttachKey)) return true;
      }
    }
    return false;
  };

  const getModelKeyForLine = (lineId, productId, group) =>
    (group === 'single' ? SINGLE_MODEL_KEY : toStr(lineId));

  const resolveModelKeyForCard = (lineId, productId, group, wrapper) => {
    if (group === 'single') return SINGLE_MODEL_KEY;
    const fixed = String(wrapper?.dataset?.c3dVendorModelId ?? '').trim();
    return fixed || getModelKeyForLine(lineId, productId, group);
  };

  const clear3DModel = async () => {
    const viewer = get3DViewer();
    getVendor()?.hidePlacementZones?.();
    if (viewer) await viewer.clearModel();
    const vendor = getVendor();
    if (vendor?.ensureAutoLoadBaseModels) await vendor.ensureAutoLoadBaseModels();
    else await vendor?.autoLoadBaseModels?.();
  };

  const show3DEmpty = () => {
    get3DViewer()?.showEmpty();
  };

  const attachModelActionBubbleDom = (viewer, bubbleEl, anchor, offset, options = {}) => {
    if (!viewer?.domTo3D?.attach || !bubbleEl) return;
    if (options.markGrace) markModelBubbleInteractionGrace();
    else if (options.touchGrace !== false) touchModelBubbleInteractionGrace();

    const reassertBubbleDom = () => {
      if (!lastModelPickForBubble || !bubbleEl) return;
      bubbleEl.style.display = 'flex';
      bubbleEl.style.visibility = 'visible';
      if (bubbleEl.classList.contains('preview-bubble--model-action--no-swatch')) {
        bubbleEl.style.justifyContent = 'center';
        bubbleEl.style.alignItems = 'center';
      }
      try {
        viewer.domTo3D.attach('customization-3d-model-bubble', bubbleEl, anchor, offset);
      } catch (_) {
      }
      if (bubbleEl.classList.contains('preview-bubble--model-action--no-swatch')) {
        bubbleEl.style.display = 'flex';
        bubbleEl.style.justifyContent = 'center';
        bubbleEl.style.alignItems = 'center';
      }
    };

    window.__C3D_PRESERVE_MODEL_BUBBLE_ON_HIDE_ALL = true;
    try {
      viewer.domTo3D.attach('customization-3d-model-bubble', bubbleEl, anchor, offset);
    } finally {
      window.__C3D_PRESERVE_MODEL_BUBBLE_ON_HIDE_ALL = false;
    }
    reassertBubbleDom();
    if (options.markGrace) {
      requestAnimationFrame(reassertBubbleDom);
      setTimeout(reassertBubbleDom, 0);
      setTimeout(reassertBubbleDom, 72);
      setTimeout(reassertBubbleDom, 160);
    }
  };

  const hideModelActionBubble = () => {
    clearModelBubbleInteractionGrace();
    lastModelPickForBubble = null;
    lastModelBubbleTrackCtx = null;
    if (moveIndicatorTrackRaf) {
      cancelAnimationFrame(moveIndicatorTrackRaf);
      moveIndicatorTrackRaf = 0;
    }
    try {
      get3DViewer()?.domTo3D?.detach?.('customization-3d-model-bubble');
      get3DViewer()?.domTo3D?.detach?.('customization-3d-model-move-indicator');
    } catch (_) {
    }
    const bubble = document.getElementById('customization-3d-model-bubble');
    if (bubble) {
      bubble.style.display = 'none';
      bubble.style.visibility = 'hidden';
      bubble.style.left = '';
      bubble.style.top = '';
    }
    const moveIcon = document.getElementById('customization-3d-model-move-indicator');
    if (moveIcon) {
      moveIcon.style.display = 'none';
      moveIcon.style.visibility = 'hidden';
      moveIcon.style.left = '';
      moveIcon.style.top = '';
    }
  };

  const ensureModelMoveIndicator = () => {
    if (!isModelMoveIndicatorEnabled()) return null;
    const host = document.querySelector('[data-3d-container]') || document.body;
    let node = document.getElementById('customization-3d-model-move-indicator');
    if (node) {
      if (node.parentNode !== host) host.appendChild(node);
      return node;
    }
    const iconUrl = String(window.CUSTOMIZATION_3D_MODEL_MOVE_ICON_URL || '').trim();
    node = document.createElement('div');
    node.id = 'customization-3d-model-move-indicator';
    node.className = 'customization-3d-model-move-indicator';
    node.style.display = 'none';
    node.innerHTML = iconUrl
      ? `<img src="${escapeAttr(iconUrl)}" alt="" width="32" height="32" decoding="async">`
      : '';
    host.appendChild(node);
    return node;
  };

  const normalizeSkuKey = (sku) => String(sku || '').trim().toLowerCase();

  const getCardSkuSet = (wrapper) => {
    const variants = getVariantData(wrapper) || [];
    const set = new Set();
    variants.forEach((v) => {
      const key = normalizeSkuKey(v?.sku);
      if (key) set.add(key);
    });
    return set;
  };

  const cardMatchesHotspotSkuFilter = (wrapper, filterSet) => {
    if (!filterSet || !filterSet.size) return true;
    const cardSkus = getCardSkuSet(wrapper);
    for (const sku of filterSet) {
      if (cardSkus.has(sku)) return true;
    }
    return false;
  };

  const getC3dState = () => document.querySelector('[data-3d-container]')?.__c3dState;

  const ensureHotspotFilterDismissControl = () => {
    const panel = document.querySelector('.secondary-panel');
    if (!panel) return null;
    let bar = panel.querySelector('[data-hotspot-filter-bar]');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'hotspot-filter-bar';
      bar.setAttribute('data-hotspot-filter-bar', '1');
      bar.innerHTML = [
        '<span class="hotspot-filter-bar__text" data-hotspot-filter-text></span>',
        '<button type="button" class="hotspot-filter-bar__btn" data-hotspot-filter-clear>Back</button>',
      ].join('');
      const scroller = panel.querySelector('.secondary-panel-content');
      if (scroller) panel.insertBefore(bar, scroller);
      else panel.insertBefore(bar, panel.firstChild);
      bar.querySelector('[data-hotspot-filter-clear]')?.addEventListener('click', () => {
        clearHotspotSkuFilter();
      });
    }
    return bar;
  };

  const updateHotspotFilterUi = () => {
    const bar = ensureHotspotFilterDismissControl();
    if (!bar) return;
    const textEl = bar.querySelector('[data-hotspot-filter-text]');
    if (activeHotspotSkuFilter && activeHotspotSkuFilter.size) {
      bar.style.display = 'flex';
      if (textEl) textEl.textContent = c3dT('hotspot_filtered');
    } else {
      bar.style.display = 'none';
      if (textEl) textEl.textContent = '';
    }
  };

  const applyHotspotSkuFilter = (skus) => {
    const list = Array.isArray(skus) ? skus : [];
    const filterSet = new Set(list.map(normalizeSkuKey).filter(Boolean));
    activeHotspotSkuFilter = filterSet.size ? filterSet : null;
    const st = getC3dState();
    if (st) refreshAllPanelFilters(st);
    updateHotspotFilterUi();
  };

  const clearHotspotSkuFilter = () => {
    activeHotspotSkuFilter = null;
    const st = getC3dState();
    if (st) refreshAllPanelFilters(st);
    updateHotspotFilterUi();
  };

  const getHotspotActionConfig = (detail = {}) => {
    const all = window.CUSTOMIZATION_3D_HOTSPOT_ACTIONS;
    if (!all || typeof all !== 'object') return null;
    return all[detail.id] || all[detail.name] || null;
  };
  const handle3DHotspotClick = (event) => {
    const detail = event?.detail || {};
    const viewer = get3DViewer();
    if (!viewer) return;

    if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS === true) {
      console.info('Hotspot click', {
        id: detail.id,
        name: detail.name,
        raw: detail,
      });
    }

    const hotspotSkus = detail?.payload?.skus || detail?.skus || [];
    if (Array.isArray(hotspotSkus) && hotspotSkus.length) {
      const filterSet = new Set(hotspotSkus.map(normalizeSkuKey).filter(Boolean));
      const panelId = resolveHotspotAccessoryPanelId(filterSet);
      const host = document.querySelector('[data-3d-container]');
      const st = host?.__c3dState;
      if (panelId && st) void activatePrimaryPanelForHotspot(panelId, st);
      applyHotspotSkuFilter(hotspotSkus);
    } else {
      clearHotspotSkuFilter();
    }

    if (typeof window.CUSTOMIZATION_3D_ON_HOTSPOT_CLICK === 'function') {
      try {
        window.CUSTOMIZATION_3D_ON_HOTSPOT_CLICK(detail, viewer);
      } catch (err) {
        console.warn('[Customization] CUSTOMIZATION_3D_ON_HOTSPOT_CLICK failed:', err);
      }
    }

    const action = getHotspotActionConfig(detail);
    if (action?.door) {
      viewer.door?.toggle(action.door);
    }
    if (action?.clipping) {
      const clip = action.clipping;
      if (typeof clip.height === 'number') {
        viewer.clipping?.setY(clip.height, clip.keepBelow !== false, Array.isArray(clip.meshNames) ? clip.meshNames : []);
      }
    }
  };

  const escapeAttr = (s) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');

  const ensureModelActionBubble = () => {
    const bubbleHost = () =>
      document.querySelector('[data-3d-container]') || document.body;
    let bubble = document.getElementById('customization-3d-model-bubble');
    if (bubble) {
      const host = bubbleHost();
      if (bubble.parentNode !== host) host.appendChild(bubble);
      return bubble;
    }
    const deleteIconUrl = String(
      window.CUSTOMIZATION_3D_MODEL_BUBBLE_DELETE_ICON_URL || ''
    ).trim();
    const deleteInner = deleteIconUrl
      ? `<img class="preview-bubble__delete-icon" src="${escapeAttr(deleteIconUrl)}" alt="" width="16" height="16" decoding="async">`
      : '';
    bubble = document.createElement('div');
    bubble.id = 'customization-3d-model-bubble';
    bubble.className = 'preview-bubble preview-bubble--model-action';
    bubble.style.display = 'none';
    bubble.innerHTML = [
      '<div class="preview-bubble__swatches"></div>',
      '<span class="preview-bubble__divider"></span>',
      `<button type="button" class="preview-bubble__delete" data-model-bubble-delete aria-label="??">${deleteInner}</button>`,
      '<span class="preview-bubble__tail"></span>',
    ].join('');
    bubble.addEventListener('pointerdown', (e) => e.stopPropagation(), true);
    bubble.addEventListener('pointerup', (e) => e.stopPropagation(), true);
    bubbleHost().appendChild(bubble);
    return bubble;
  };

  const getActivePanel3dBubbleMode = () => {
    const panel = document.querySelector('.panel-content.active');
    return normalizeC3dBubbleMode(panel?.dataset?.c3dPanelBubble || 'color');
  };

  const getModelControlBubbleMode = (detail, pickedMesh) => {
    const raw = String(
      detail?.modelControlType
      || detail?.model_control_type
      || detail?.controlType
      || detail?.model_control
      || pickedMesh?.metadata?.modelControlType
      || pickedMesh?.metadata?.model_control_type
      || ''
    ).trim().toLowerCase();
    if (raw === 'rotation' || raw === 'rotate' || raw === 'furniture') return 'rotate';
    if (raw === 'color' || raw === 'colour') return 'color';
    return '';
  };

  const applyModelBubbleShellByMode = (bubble, mode, canDelete = true, options = {}) => {
    const showColorSwatches = options.showColorSwatches === true;
    const deleteIconUrl = String(window.CUSTOMIZATION_3D_MODEL_BUBBLE_DELETE_ICON_URL || '').trim();
    const deleteInner = deleteIconUrl
      ? `<img class="preview-bubble__delete-icon" src="${escapeAttr(deleteIconUrl)}" alt="" width="16" height="16" decoding="async">`
      : '';
    const rotateIconUrl = String(window.CUSTOMIZATION_3D_MODEL_BUBBLE_ROTATE_ICON_URL || '').trim();
    const rotateSize = 32;
    const rotateInner = rotateIconUrl
      ? `<img class="preview-bubble__rotate-icon" src="${escapeAttr(rotateIconUrl)}" alt="" width="${rotateSize}" height="${rotateSize}" decoding="async">`
      : '';

    bubble.classList.toggle('preview-bubble--model-action--rotate', mode === 'rotate');
    bubble.classList.toggle('preview-bubble--model-action--color', mode !== 'rotate');

    if (mode === 'rotate') {
      const rotateDeleteIconUrl = String(window.CUSTOMIZATION_3D_MODEL_BUBBLE_ROTATE_DELETE_ICON_URL || '').trim();
      const rotateDeleteInner = `<img class="preview-bubble__delete-icon" src="${escapeAttr(rotateDeleteIconUrl)}" alt="" width="32" height="32" decoding="async">`;
      bubble.innerHTML = [
        `<button type="button" class="preview-bubble__rotate preview-bubble__rotate--solo" data-model-bubble-rotate aria-label="Rotate 90?">${rotateInner}</button>`,
        canDelete ? '<span class="preview-bubble__divider"></span>' : '',
        canDelete ? `<button type="button" class="preview-bubble__delete" data-model-bubble-delete aria-label="??">${rotateDeleteInner}</button>` : '',
      ].join('');
      bubble.style.justifyContent = canDelete ? '' : 'center';
      bubble.style.minWidth = canDelete ? '' : '0';
      bubble.style.width = canDelete ? '' : 'fit-content';
      return;
    }

    const swatchBlock = showColorSwatches
      ? '<div class="preview-bubble__swatches"></div>'
      : '';
    const dividerBlock = showColorSwatches && canDelete
      ? '<span class="preview-bubble__divider"></span>'
      : '';
    const deleteOnly = mode !== 'rotate' && !showColorSwatches && canDelete;
    bubble.innerHTML = [
      swatchBlock,
      dividerBlock,
      canDelete ? `<button type="button" class="preview-bubble__delete" data-model-bubble-delete aria-label="??">${deleteInner}</button>` : '',
      deleteOnly ? '' : '<span class="preview-bubble__tail"></span>',
    ].join('');
    bubble.classList.toggle('preview-bubble--model-action--no-swatch', deleteOnly || (mode !== 'rotate' && !showColorSwatches));
    if (deleteOnly) {
      bubble.style.justifyContent = 'center';
      bubble.style.alignItems = 'center';
      bubble.style.minWidth = '';
      bubble.style.width = '';
    } else {
      bubble.style.justifyContent = (canDelete || showColorSwatches) ? '' : 'center';
      bubble.style.minWidth = (canDelete || showColorSwatches) ? '' : '0';
      bubble.style.width = (canDelete || showColorSwatches) ? '' : 'fit-content';
      bubble.style.alignItems = '';
    }
  };

  const setupModelActionBubbleEvents = () => {
    if (document.documentElement.dataset.customization3dModelBubbleActionsBound === '1') return;
    document.documentElement.dataset.customization3dModelBubbleActionsBound = '1';
    document.addEventListener('click', async (e) => {
      if (e.target.closest('#customization-3d-model-bubble')) {
        e.stopPropagation();
      }
      const colorBtn = e.target.closest('#customization-3d-model-bubble .preview-bubble__swatch');
      if (colorBtn) {
        if (colorBtn.disabled || colorBtn.classList.contains('preview-bubble__swatch--disabled')) return;
        const bubble = colorBtn.closest('#customization-3d-model-bubble');
        const host = document.querySelector('[data-3d-container]');
        const st = host && host.__c3dState;
        const optVal = String(colorBtn.dataset.modelBubbleOptionValue || '').trim();
        const viewer = get3DViewer();
        if (bubble && st && optVal && viewer) {
          const pickAttachKey = String(
            lastModelPickForBubble?.attachKey
            || viewer.resolveAttachKeyFromPick?.(lastModelPickForBubble || null)
            || ''
          ).trim();
          let ctx = resolveModelBubbleLineContext(st, pickAttachKey);
          if ((!ctx.lineId || !ctx.productId) && lastModelPickForBubble?.isPrimaryProduct) {
            const single = Array.from(st.get().entries()).find(([, data]) => data?.group === 'single');
            if (single) {
              ctx = {
                lineId: String(single[0]),
                productId: String(single[1]?.productId || '') || null,
              };
            }
          }
          const lineId = ctx.lineId;
          const wrapper = ctx.productId
            ? document.querySelector(`.product-card-clickable[data-product-id="${String(ctx.productId)}"]`)
            : null;
          const swatchList = wrapper?.querySelector('[data-cpc-swatch-list]');
          const optionPos = swatchList?.dataset?.optionPosition;
          if (lineId && wrapper && optionPos) {
            const lineData = st.get().get(lineId);
            if (lineData) {
              const picked = pickVariantForColor(wrapper, lineData.variantId, optionPos, optVal, st, lineId);
              if (!picked) {
                showNotification(c3dT('not_enough_color_inventory'), 'warning');
                return;
              }
              const applied = await applyVariantChangeToExistingModel(st, lineId, wrapper, picked);
              if (!applied) {
                showNotification(c3dT('color_apply_failed'), 'warning');
                return;
              }
              if (bubble) {
                const fresh = st.get().get(lineId);
                const sw = getPreviewSwatchCandidates(wrapper, st, lineId);
                fillModelActionBubbleSwatches(bubble, wrapper, fresh, sw.items, sw.optionPos);
              }
              return;
            }
          }
        }
        if (bubble && !optVal) {
          bubble.querySelectorAll('.preview-bubble__swatch').forEach((btn) => {
            btn.classList.toggle('preview-bubble__swatch--selected', btn === colorBtn);
          });
        }
        return;
      }
      const rotateBtn = e.target.closest('[data-model-bubble-rotate]');
      if (rotateBtn) {
        const viewer = get3DViewer();
        const degRaw = window.CUSTOMIZATION_3D_MESH_ROTATE_DEGREES;
        const deg = Number(degRaw);
        const yDeg = Number.isFinite(deg) ? deg : 90;
        const modelId = String(
          viewer?.resolveAttachKeyFromPick?.(lastModelPickForBubble || null)
          || lastModelPickForBubble?.attachKey
          || ''
        ).trim();
        let ok = await viewer?.transform?.rotationByModelId?.(modelId, { x: 0, y: yDeg, z: 0 });
        if (ok !== true) {
          const radians = (yDeg * Math.PI) / 180;
          ok = viewer?.meshRotate?.aroundWorldYFromPick?.(lastModelPickForBubble, radians);
        }
        if (ok !== true) {
          console.warn('[3D] Rotate action did not apply; check mesh / vendor scene.', {
            modelId,
            meshName: lastModelPickForBubble?.meshName,
            hasPick: !!lastModelPickForBubble,
          });
        }
        return;
      }
      const deleteBtn = e.target.closest('[data-model-bubble-delete]');
      if (deleteBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (lastModelPickForBubble?.canDelete === false) return;
        const viewer = get3DViewer();
        const host = document.querySelector('[data-3d-container]');
        const st = host?.__c3dState;
        const pickAttachKey = String(
          lastModelPickForBubble?.attachKey
          || lastModelPickForBubble?.registeredUnloadKey
          || ''
        ).trim();
        const bubbleCtx = st ? resolveModelBubbleLineContext(st, pickAttachKey) : { lineId: null };
        const lineIdToRemove = bubbleCtx.lineId ? String(bubbleCtx.lineId) : '';
        const lineRow = lineIdToRemove && st ? st.get().get(lineIdToRemove) : null;
        const stateModelKey = String(lineRow?.modelKey || '').trim();
        const deleteFallbackKey = String(
          stateModelKey
          || pickAttachKey
          || lastModelPickForBubble?.registeredUnloadKey
          || lastModelPickForBubble?.attachKey
          || ''
        ).trim();
        const pickSnapshot = lastModelPickForBubble
          ? {
            ...lastModelPickForBubble,
            attachKey: deleteFallbackKey || lastModelPickForBubble.attachKey,
            registeredUnloadKey: deleteFallbackKey || lastModelPickForBubble.registeredUnloadKey,
          }
          : (deleteFallbackKey ? { attachKey: deleteFallbackKey, registeredUnloadKey: deleteFallbackKey } : null);
        const run = (async () => {
          let result = await viewer?.removeModelByPick?.(pickSnapshot);
          if (!result?.ok && deleteFallbackKey && viewer?.removeRegisteredModelByKey) {
            const removed = await viewer.removeRegisteredModelByKey(deleteFallbackKey);
            if (removed) result = { ok: true, modelName: deleteFallbackKey };
          }
          if (!result?.ok) {
            console.warn('[C3D] model delete failed', {
              meshName: pickSnapshot?.meshName,
              modelName: result?.modelName,
              deleteFallbackKey,
            });
            return;
          }
          hideModelActionBubble();
          if (!st) return;
          if (lineIdToRemove && st.get().has(lineIdToRemove)) {
            st.remove(lineIdToRemove);
            return;
          }
          const deletedKey = String(result.modelName || '').trim();
          if (!deletedKey) return;
          st.get().forEach((data, lineId) => {
            if (String(data?.modelKey || '').trim() === deletedKey) st.remove(lineId);
          });
        })();
        Promise.resolve(run).finally(() => {
          clearPending3dPlacement();
          hideModelActionBubble();
        });
      }
    }, true);
  };

  const computeModelBubblePlacement = (pickedMesh, pickedPoint, mode, isPrimaryProduct) => {
    const m = pickedMesh;
    const p = pickedPoint;
    const bbox = m?.getBoundingInfo?.()?.boundingBox;
    const center = bbox?.centerWorld || p || { x: 0, y: 0, z: 0 };
    const bottomCenter = bbox
      ? [
        Number((center?.x ?? p?.x) || 0),
        Number((bbox.minimumWorld?.y ?? p?.y) || 0),
        Number((center?.z ?? p?.z) || 0),
      ]
      : [
        Number(p?.x || 0),
        Number(p?.y || 0),
        Number(p?.z || 0),
      ];
    const topCenter = bbox
      ? [
        Number((center?.x ?? p?.x) || 0),
        Number((bbox.maximumWorld?.y ?? p?.y) || 0),
        Number((center?.z ?? p?.z) || 0),
      ]
      : [
        Number(p?.x || 0),
        Number(p?.y || 0),
        Number(p?.z || 0),
      ];
    const bubbleAnchor = (mode === 'color' && isPrimaryProduct) ? topCenter : bottomCenter;
    const bubbleOffset =
      (mode === 'color' && isPrimaryProduct)
        ? { x: 0, y: -38 }
        : { x: 0, y: -24 };
    return { bubbleAnchor, bubbleOffset, bottomWorld: bottomCenter };
  };

  const handle3DModelClick = (event) => {
    const detail = event?.detail || {};
    const viewer = get3DViewer();
    if (!viewer) return;

    const pickedMesh = detail.pickedMesh || null;
    const pickedPoint = detail.pickedPoint || null;
    if (
      viewer.isSceneEnvironmentMesh?.(pickedMesh)
      || viewer.isSceneEnvironmentMeshName?.(detail.meshName || pickedMesh?.name)
    ) {
      hideModelActionBubble();
      return;
    }
    if (!pickedMesh || !pickedPoint) {
      hideModelActionBubble();
      return;
    }

    const materialName = String(
      detail.materialName || detail.material || pickedMesh.material?.name || ''
    ).trim();
    if (materialName) window.CUSTOMIZATION_3D_LAST_MATERIAL_NAME = materialName;

    const sdkPickModelName = viewer?.findSdkModelNameByMesh?.(pickedMesh) || '';
    const rawPickAttachKey = viewer?.resolveAttachKeyFromPick?.(detail) || '';
    const strictTrackedKey = viewer?.findTrackedModelKeyForMesh?.(pickedMesh) || '';
    let pickAttachKey = /_\d{10,}$/.test(String(sdkPickModelName || '').trim())
      ? String(sdkPickModelName).trim()
      : (strictTrackedKey || rawPickAttachKey);
    if (window.CUSTOMIZATION_3D_DEBUG_PICK === true) {
      console.info('[C3D pick]', {
        sdkPickModelName,
        rawPickAttachKey,
        strictTrackedKey,
        finalPickAttachKey: pickAttachKey,
        meshName: String(detail.meshName || pickedMesh?.name || ''),
        trackedKeys: viewer?.listViewerTrackedModelKeys?.(),
        kernelDicKeys: viewer?.listKernelModelDicKeys?.(),
        debugFromViewer: viewer?.debugFindRegisteredKeyAfterPick?.(),
      });
    }
    const host3d = document.querySelector('[data-3d-container]');
    const st3d = host3d?.__c3dState;

    if (pending3dPlacement) {
      const pendingPickKey = String(pickAttachKey || sdkPickModelName || '').trim();
      const isPlacedAccessoryPick = /_\d{10,}$/.test(pendingPickKey)
        && Boolean(resolveModelBubbleLineContext(st3d, pendingPickKey).lineId);
      if (!isPlacedAccessoryPick) {
        hideModelActionBubble();
        return;
      }
    }

    const bubbleCtx = resolveModelBubbleLineContext(st3d, pickAttachKey);
    const rowForUnload = bubbleCtx.lineId ? st3d?.get?.()?.get?.(bubbleCtx.lineId) : null;
    const accessorySkuForZones = resolveAccessorySkuFromStateLine(st3d, bubbleCtx.lineId);
    if (accessorySkuForZones) {
      void getVendor()?.syncPlacementDivisionsForSku?.(accessorySkuForZones);
    }
    const registeredUnloadKey = rowForUnload?.modelKey
      ? String(rowForUnload.modelKey)
      : (bubbleCtx.lineId ? String(bubbleCtx.lineId) : '');
    const isSdkAccessoryPick = /_\d{10,}$/.test(String(sdkPickModelName || pickAttachKey || '').trim());
    let isPrimaryProduct = isSdkAccessoryPick ? false : isPrimaryProductBubbleContext(
      st3d,
      rowForUnload,
      pickAttachKey,
      bubbleCtx.productId
    );
    const sdkBubbleMode = getModelControlBubbleMode(detail, pickedMesh);
    if (!isSdkAccessoryPick && sdkBubbleMode === 'color' && st3d) {
      const hasSingle = Array.from(st3d.get().values()).some((data) => data?.group === 'single');
      if (hasSingle && !rowForUnload) isPrimaryProduct = true;
    }
    const canDelete = !isPrimaryProduct;
    lastModelPickForBubble = {
      pickedMesh,
      pickedPoint,
      meshName: String(detail.meshName || pickedMesh?.name || '').trim(),
      attachKey: pickAttachKey,
      registeredUnloadKey: registeredUnloadKey || pickAttachKey,
      detail,
      canDelete,
      isPrimaryProduct,
    };
    try {
      window.__c3dLastPickedMesh = pickedMesh;
    } catch (_) {
    }
    const bubble = ensureModelActionBubble();
    let mode = getModelControlBubbleMode(detail, pickedMesh);
    if (!mode && rowForUnload?.bubbleMode) {
      mode = rowForUnload.bubbleMode;
    } else if (!mode && pickAttachKey && modelBubbleModeMap.has(pickAttachKey)) {
      mode = modelBubbleModeMap.get(pickAttachKey);
    } else if (!mode && bubbleCtx.lineId) {
      const fallbackRow = st3d?.get?.()?.get?.(bubbleCtx.lineId);
      mode = fallbackRow?.bubbleMode || getProductBubbleModeById(fallbackRow?.productId || '') || lastLoadedProductBubbleMode;
    } else if (!mode) {
      mode = lastLoadedProductBubbleMode;
    }
    let bubbleLineId = bubbleCtx.lineId;
    let bubbleProductWrapper = bubbleCtx.productId
      ? document.querySelector(`.product-card-clickable[data-product-id="${String(bubbleCtx.productId)}"]`)
      : null;
    if (isPrimaryProduct && st3d && (!bubbleProductWrapper || bubbleLineId == null)) {
      const single = Array.from(st3d.get().entries()).find(([, data]) => data?.group === 'single');
      if (single) {
        bubbleLineId = String(single[0]);
        const pid = String(single[1]?.productId || '').trim();
        if (pid) {
          bubbleProductWrapper = document.querySelector(
            `.product-card-clickable[data-product-id="${pid}"]`
          );
        }
      }
    }
    const hasColorSwatches = modelBubbleHasColorSwatches(
      bubbleProductWrapper,
      st3d,
      bubbleLineId
    );
    if (isPrimaryProduct) {
      if (!hasColorSwatches) {
        hideModelActionBubble();
        return;
      }
      mode = 'color';
    }
    applyModelBubbleShellByMode(bubble, mode, canDelete, {
      showColorSwatches: mode === 'color' && hasColorSwatches,
    });
    if (mode === 'color' && hasColorSwatches) {
      const synced = syncModelActionBubbleSwatchesFromPrimary(bubble);
      if (!synced) {
        hideModelActionBubble();
        return;
      }
    }
    const hasBubbleActions = !!(
      bubble.querySelector('.preview-bubble__swatch')
      || bubble.querySelector('[data-model-bubble-delete]')
      || bubble.querySelector('[data-model-bubble-rotate]')
    );
    if (!hasBubbleActions) {
      hideModelActionBubble();
      return;
    }
    bubble.style.visibility = 'visible';
    bubble.style.display = 'flex';
    markModelBubbleInteractionGrace();
    lastModelBubbleTrackCtx = { mode, isPrimaryProduct };
    const placement = computeModelBubblePlacement(
      lastModelPickForBubble?.pickedMesh || detail.pickedMesh,
      lastModelPickForBubble?.pickedPoint || pickedPoint,
      mode,
      isPrimaryProduct
    );
    attachModelActionBubbleDom(
      viewer,
      bubble,
      placement.bubbleAnchor,
      placement.bubbleOffset,
      { markGrace: true }
    );

    const moveIcon = ensureModelMoveIndicator();
    const shouldShowMoveIndicator = isModelMoveIndicatorEnabled() && mode === 'color' && canDelete;
    if (moveIndicatorTrackRaf) {
      cancelAnimationFrame(moveIndicatorTrackRaf);
      moveIndicatorTrackRaf = 0;
    }
    if (!shouldShowMoveIndicator) {
      try {
        viewer.domTo3D?.detach?.('customization-3d-model-move-indicator');
      } catch (_) {
      }
      if (moveIcon) {
        moveIcon.style.display = 'none';
        moveIcon.style.visibility = 'hidden';
      }
    }
    const trackModelActionDomAttachments = () => {
      const v = get3DViewer();
      const pick = lastModelPickForBubble;
      const ctx = lastModelBubbleTrackCtx;
      const bubbleEl = document.getElementById('customization-3d-model-bubble');
      if (!v || !pick || !ctx || !bubbleEl || bubbleEl.style.display === 'none') {
        moveIndicatorTrackRaf = 0;
        return;
      }
      const next = computeModelBubblePlacement(
        pick.pickedMesh,
        pick.pickedPoint,
        ctx.mode,
        ctx.isPrimaryProduct
      );
      attachModelActionBubbleDom(
        v,
        bubbleEl,
        next.bubbleAnchor,
        next.bubbleOffset,
        { touchGrace: true }
      );
      const showMove =
        isModelMoveIndicatorEnabled() && ctx.mode === 'color' && pick.canDelete && !pick.isPrimaryProduct;
      const icon = document.getElementById('customization-3d-model-move-indicator');
      if (showMove && icon) {
        icon.style.visibility = 'visible';
        icon.style.display = 'flex';
        v.domTo3D?.attach?.(
          'customization-3d-model-move-indicator',
          icon,
          next.bottomWorld,
          { x: 0, y: 22 }
        );
      } else {
        try {
          v.domTo3D?.detach?.('customization-3d-model-move-indicator');
        } catch (_) {
        }
        if (icon) {
          icon.style.display = 'none';
          icon.style.visibility = 'hidden';
        }
      }
      moveIndicatorTrackRaf = requestAnimationFrame(trackModelActionDomAttachments);
    };
    moveIndicatorTrackRaf = requestAnimationFrame(trackModelActionDomAttachments);
  };
  const setup3DEventBridge = (state) => {
    if (document.documentElement.dataset.customization3dEventsBound === '1') return;
    document.documentElement.dataset.customization3dEventsBound = '1';
    let hotspotRenderTimer = null;
    const renderConfiguredHotspots = () => {
      if (hotspotRenderTimer) clearTimeout(hotspotRenderTimer);
      hotspotRenderTimer = setTimeout(async () => {
        hotspotRenderTimer = null;
        await getVendor()?.fetchAndRenderHotspots?.();
      }, 200);
    };

    document.addEventListener('3d:vendor:model-placed', (e) => {
      const modelId = String(e?.detail?.modelId || '').trim();
      if (modelId && pending3dPlacement) {
        pending3dPlacement.lastPlacedModelId = modelId;
      }
      ensureHotspotsLoadedOnce();
    });

    document.addEventListener('3d:dropzone:placing', () => {
      if (!pending3dPlacement) return;
      pending3dPlacement.isPlacing = true;
    });

    document.addEventListener('3d:dropzone:placed', (e) => {
      const detail = e?.detail || {};
      const placementResult = detail.placementResult || {};
      syncStateWithRemovedModelKeys(state, placementResult.removedModelIds);
      const placedModelId = Array.isArray(placementResult.modelIds)
        ? String(placementResult.modelIds[0] || '').trim()
        : String(pending3dPlacement?.lastPlacedModelId || '').trim();
      if (placementResult.ok) {
        if (pending3dPlacement) {
          commitPendingPlacementToState(state, placedModelId, placementResult.sku);
        } else {
          const placedSku = String(placementResult.sku || '').trim();
          if (placedSku) {
            getVendor()?.commitPlacementZoneDivisions?.(null, placedSku);
          }
          getVendor()?.hidePlacementZones?.();
          show3DSceneActive();
        }
        return;
      }
      console.warn('[C3D] dropzone placement failed', placementResult);
      if (placedModelId && pending3dPlacement) {
        commitPendingPlacementToState(state, placedModelId, placementResult.sku);
        return;
      }
      if (pending3dPlacement) {
        cancelPending3dPlacement();
      }
    });

    document.addEventListener('3d:scene:ready', () => {
      if (window.CUSTOMIZATION_3D_DEFER_HOTSPOTS === false) {
        renderConfiguredHotspots();
      }
    });
    document.addEventListener('3d:hotspot:click', handle3DHotspotClick);
    document.addEventListener('3d:model:click', handle3DModelClick);
    document.addEventListener('3d:dom-to-3d-hide-all', () => {
      clearModelBubbleInteractionGrace();
      hideModelActionBubble();
    });
    document.addEventListener('3d:canvas:blank-click', () => {
      clearModelBubbleInteractionGrace();
      hideModelActionBubble();
      if (placementZonesRevealInProgress) return;
      if (!pending3dPlacement) return;
      if (pending3dPlacement.isPlacing) return;
      cancelPending3dPlacement();
    });
    setupModelActionBubbleEvents();
  };

  const setup3DContainerInteractionGuards = () => {
    const container = document.querySelector('[data-3d-container]');
    if (!container || container.dataset.customization3dInteractionGuards === '1') return;
    container.dataset.customization3dInteractionGuards = '1';

    const allowNativeUI = (target) =>
      Boolean(target?.closest?.('.preview-header__actions button, .preview-header__actions a'));

    container.addEventListener(
      'wheel',
      (e) => {
        if (!container.contains(e.target)) return;
        e.preventDefault();
      },
      { capture: true, passive: false }
    );

    container.addEventListener('selectstart', (e) => {
      if (allowNativeUI(e.target)) return;
      e.preventDefault();
    });

    container.addEventListener('contextmenu', (e) => {
      if (allowNativeUI(e.target)) return;
      e.preventDefault();
    });

    const canvas = document.getElementById('preview-3d-viewer');
    if (canvas?.tagName === 'CANVAS') {
      canvas.setAttribute('draggable', 'false');
    }
  };

  const clearCustomizationSessionDraft = () => {
    try {
      sessionStorage.removeItem(C3D_SESSION_DRAFT_KEY);
    } catch {
      /* ignore */
    }
  };

  const persistCustomizationSessionDraft = (_state) => {
    clearCustomizationSessionDraft();
  };

  const removeLinesFromCustomizationState = (state, lineIds) => {
    lineIds.forEach((id) => state.remove(id));
    persistCustomizationSessionDraft(state);
  };

  const createState = () => {
    const selected = new Map();
    const listeners = new Set();
    let batchDepth = 0;
    let pendingNotify = false;
    const notify = () => {
      if (batchDepth > 0) { pendingNotify = true; return; }
      listeners.forEach(fn => fn());
    };

    return {
      add:       (id, data) => { selected.set(id, data); notify(); },
      remove:    (id)       => { selected.delete(id);    notify(); },
      clear:     ()         => { selected.clear();       notify(); },
      get:       ()         => selected,
      has:       (id)       => selected.has(id),
      subscribe: (fn)       => listeners.add(fn),
      batch:     (fn)       => {
        batchDepth++;
        try { fn(); } finally {
          batchDepth--;
          if (batchDepth === 0 && pendingNotify) {
            pendingNotify = false;
            listeners.forEach(f => f());
          }
        }
      },
    };
  };

  const toStr = (v) => String(v == null ? '' : v).trim();
  const createLineId = (productId, variantId) => `${toStr(productId)}::${toStr(variantId)}::${Date.now()}::${Math.random().toString(36).slice(2, 8)}`;
  const getProductTotalQty = (state, productId) => {
    const target = toStr(productId);
    let total = 0;
    state.get().forEach((data) => {
      if (toStr(data.productId) === target) total += Math.max(1, parseInt(data.quantity, 10) || 1);
    });
    return total;
  };
  const hasProductSelected = (state, productId) => getProductTotalQty(state, productId) > 0;


  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;


  const showNotification = (message, type = 'warning') => {
    document.querySelector('.customization-3d-notification')?.remove();

    const el = document.createElement('div');
    el.className = `customization-3d-notification customization-3d-notification--${type}`;
    el.textContent = message;

    if (!document.querySelector('#customization-3d-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'customization-3d-notification-styles';
      style.textContent = `
        .customization-3d-notification {
          position: fixed; top: 20px; right: 20px;
          padding: 12px 20px; background: #E37E16; color: white;
          border-radius: 4px; z-index: 200000;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          animation: slideInNotification 0.3s ease;
          font-family: system-ui, -apple-system, sans-serif; font-size: 14px;
        }
        .customization-3d-notification--error   { background: #AA2826; }
        .customization-3d-notification--success { background: #708A5C; }
        @keyframes slideInNotification {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes slideOutNotification {
          from { transform: translateX(0);    opacity: 1; }
          to   { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(el);
    setTimeout(() => {
      if (!el.parentNode) return;
      el.style.animation = 'slideOutNotification 0.3s ease';
      setTimeout(() => el.remove(), 300);
    }, 3000);
  };


  const getVariantData = (wrapper) => {
    try {
      const script = wrapper.querySelector('script[type="application/json"][data-product-variants]');
      return script ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  };

  const resolveAccessorySkuFromStateLine = (state, lineId) => {
    if (!state || !lineId) return '';
    const data = state.get().get(lineId);
    if (!data || data.group === 'single') return '';
    const wrapper =
      document.querySelector(
        `.product-card-clickable[data-product-id="${CSS.escape(String(data.productId))}"][data-selection-group="multi"]`
      )
      || document.querySelector(
        `.product-card-clickable[data-product-id="${CSS.escape(String(data.productId))}"]`
      );
    const variant = wrapper ? getVariantById(wrapper, data.variantId) : null;
    return String(variant?.sku || '').trim();
  };

  const getVariantById = (wrapper, variantId) => {
    const variants = getVariantData(wrapper);
    return variants?.find(v => String(v.id) === String(variantId)) || null;
  };

  const getVariantInventoryCap = (variant) => {
    if (!variant || variant.available === false) return 0;
    if (!variant.inventory_management) return Infinity;
    const q = Number(variant.inventory_quantity);
    if (Number.isNaN(q) || q < 0) return 0;
    return q;
  };

  const getUsedQtyForVariant = (state, productId, variantId) => {
    const pid = toStr(productId);
    const vid = toStr(variantId);
    let n = 0;
    state.get().forEach((data) => {
      if (toStr(data.productId) === pid && toStr(data.variantId) === vid) {
        n += Math.max(1, parseInt(data.quantity, 10) || 1);
      }
    });
    return n;
  };

  const canAccommodateNewLine = (state, wrapper, variantId) => {
    const variant = getVariantById(wrapper, variantId);
    if (!variant || variant.available === false) return false;
    const cap = getVariantInventoryCap(variant);
    if (cap === Infinity) return true;
    const used = getUsedQtyForVariant(state, wrapper.dataset.productId, variantId);
    return used + 1 <= cap;
  };

  const canAccommodateLineSwitch = (state, wrapper, newVariantId, lineId) => {
    const variant = getVariantById(wrapper, newVariantId);
    if (!variant || variant.available === false) return false;
    const cap = getVariantInventoryCap(variant);
    if (cap === Infinity) return true;
    const line = state.get().get(lineId);
    const lineQty = line ? Math.max(1, parseInt(line.quantity, 10) || 1) : 1;
    let used = getUsedQtyForVariant(state, wrapper.dataset.productId, newVariantId);
    if (line && toStr(line.productId) === toStr(wrapper.dataset.productId) && toStr(line.variantId) === toStr(newVariantId)) {
      used -= lineQty;
    }
    return used + lineQty <= cap;
  };

  const isSingleSelectionGroup = (wrapper) =>
    (wrapper?.dataset?.selectionGroup || 'multi') === 'single';

  const getSingleLineEntryForProduct = (state, productId) => {
    const pid = toStr(productId);
    return Array.from(state.get().entries()).find(
      ([, data]) => data?.group === 'single' && toStr(data.productId) === pid
    ) || null;
  };

  const canUseVariantOnCard = (state, wrapper, variantId) => {
    const variant = getVariantById(wrapper, variantId);
    if (!variant || variant.available === false) return false;
    if (isSingleSelectionGroup(wrapper)) {
      const entry = getSingleLineEntryForProduct(state, wrapper.dataset.productId);
      if (entry) {
        return canAccommodateLineSwitch(state, wrapper, variantId, String(entry[0]));
      }
      return canAccommodateNewLine(state, wrapper, variantId);
    }
    return canAccommodateNewLine(state, wrapper, variantId);
  };

  const variantsMatchingColor = (variants, optionPos, colorValue) => {
    const key = `option${optionPos}`;
    const cv = toStr(colorValue);
    return (variants || []).filter((v) => toStr(v[key]) === cv);
  };

  const colorAllowsNewAdd = (state, wrapper, optionPos, colorValue) => {
    const variants = getVariantData(wrapper) || [];
    return variantsMatchingColor(variants, optionPos, colorValue).some(
      (v) => v.available !== false && canUseVariantOnCard(state, wrapper, v.id)
    );
  };

  const canAddAnyLine = (state, wrapper) => {
    const variants = getVariantData(wrapper) || [];
    if (!variants.length) return false;

    if (isSingleSelectionGroup(wrapper)) {
      if (getSingleLineEntryForProduct(state, wrapper.dataset.productId)) {
        return true;
      }
      const hasOtherSingle = Array.from(state.get().values()).some((d) => d?.group === 'single');
      if (hasOtherSingle) {
        return variants.some((v) => v.available !== false && canAccommodateNewLine(state, wrapper, v.id));
      }
    }

    const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
    if (!swatchList) {
      return variants.some((v) => v.available !== false && canUseVariantOnCard(state, wrapper, v.id));
    }
    const pos = parseInt(swatchList.dataset.optionPosition, 10);
    if (Number.isNaN(pos)) {
      return variants.some((v) => v.available !== false && canUseVariantOnCard(state, wrapper, v.id));
    }
    const seen = new Set();
    variants.forEach((v) => {
      const cv = v[`option${pos}`];
      if (cv != null && String(cv).trim() !== '') seen.add(String(cv).trim());
    });
    for (const colorValue of seen) {
      if (colorAllowsNewAdd(state, wrapper, pos, colorValue)) return true;
    }
    return false;
  };

  const getOptionsFromVariant = (variant) => {
    if (!variant) return {};
    return ['option1', 'option2', 'option3'].reduce((acc, key) => {
      const val = variant[key];
      if (val != null && String(val).trim() !== '') acc[key] = String(val).trim();
      return acc;
    }, {});
  };

  const getCardShowcaseVariantId = (wrapper) => {
    if (!wrapper) return null;
    const variants = getVariantData(wrapper);
    if (!variants?.length) return null;

    const cpcChecked = wrapper.querySelector('[data-cpc-swatch-input]:checked');
    if (cpcChecked) {
      const pos = parseInt(cpcChecked.dataset.optionPosition, 10);
      if (Number.isNaN(pos)) return null;
      const key = `option${pos}`;
      const val = cpcChecked.value;
      const matching = variants.filter(
        (v) => String(v[key] || '').trim() === String(val || '').trim()
      );
      if (!matching.length) return null;
      const savedId = wrapper.dataset.variantId;
      const pref = matching.find((v) => String(v.id) === String(savedId));
      return (pref || matching[0]).id;
    }

    const selectedOptions = {};
    wrapper.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      const parts = radio.name.split('-');
      const idx = parts[parts.length - 1];
      if (idx && !isNaN(idx)) selectedOptions[`option${idx}`] = radio.value;
    });

    if (!Object.keys(selectedOptions).length) {
      if (variants.length === 1) return variants[0].id;
      const sid = wrapper.dataset.variantId;
      if (sid && variants.some((v) => String(v.id) === String(sid))) return sid;
      return null;
    }

    const matching = variants.filter((v) =>
      Object.keys(selectedOptions).every((key) => {
        const sel = selectedOptions[key];
        const vVal = v[key];
        return sel != null && vVal != null && String(sel).trim() === String(vVal).trim();
      })
    );
    if (!matching.length) return null;
    const savedId = wrapper.dataset.variantId;
    const pref = matching.find((v) => String(v.id) === String(savedId));
    return (pref || matching[0]).id;
  };

  const getCurrentVariantId = (wrapper, _state = null) => {
    if (!wrapper) return null;
    const wrapper3D = document.querySelector('.customization-3d-wrapper');
    if (!wrapper3D?.contains(wrapper)) return null;
    return getCardShowcaseVariantId(wrapper);
  };

  const updateCpcMainImage = (wrapper, variant) => {
    const img = wrapper?.querySelector('[data-cpc-main-image]');
    if (!img || !variant) return;
    const url = variant.image || wrapper.dataset.productImage;
    if (!url) return;
    img.src = url;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    wrapper.dataset.productImage = url;
  };

  const updateCpcMetaRowVisibility = (wrapper, state) => {
    const metaRowsContainer = wrapper.querySelector('[data-cpc-meta-rows]');
    if (!metaRowsContainer) return;
    
    const productId = wrapper.dataset.productId;
    const variants = getVariantData(wrapper);
    if (!variants) return;
    
    const selectionGroup = wrapper.dataset.selectionGroup || 'multi';
    const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
    const optionPos = swatchList?.dataset.optionPosition;
    
    if (selectionGroup === 'single') {
      const totalQty = getProductTotalQty(state, productId);
      metaRowsContainer.style.display = totalQty > 0 ? '' : 'none';
      
      if (totalQty > 0) {
        if (optionPos) {
          const colorCounts = new Map();
          state.get().forEach((data) => {
            if (toStr(data.productId) !== toStr(productId)) return;
            const variant = variants.find(v => String(v.id) === String(data.variantId));
            if (!variant) return;
            const colorValue = variant[`option${optionPos}`];
            if (colorValue) {
              const qty = Math.max(1, parseInt(data.quantity, 10) || 1);
              colorCounts.set(colorValue, (colorCounts.get(colorValue) || 0) + qty);
            }
          });
          
          metaRowsContainer.innerHTML = '';
          colorCounts.forEach((count, colorValue) => {
            const row = document.createElement('div');
            row.className = 'cpc__meta-row';
            row.innerHTML = `
              <span class="cpc__color-name">${colorValue}</span>
              <span class="cpc__qty-value">${CPC_QTY_MULT}${count}</span>
            `;
            metaRowsContainer.appendChild(row);
          });
        } else {
          metaRowsContainer.innerHTML = `
            <div class="cpc__meta-row">
              <span class="cpc__color-name"></span>
              <span class="cpc__qty-value">${CPC_QTY_MULT}${totalQty}</span>
            </div>
          `;
        }
      }
      return;
    }
    
    if (!optionPos) {
      const totalQty = getProductTotalQty(state, productId);
      metaRowsContainer.style.display = totalQty > 0 ? '' : 'none';
      if (totalQty > 0) {
        metaRowsContainer.innerHTML = `
          <div class="cpc__meta-row">
            <span class="cpc__color-name"></span>
            <span class="cpc__qty-value">${CPC_QTY_MULT}${totalQty}</span>
          </div>
        `;
      }
      return;
    }
    
    const colorCounts = new Map();
    state.get().forEach((data) => {
      if (toStr(data.productId) !== toStr(productId)) return;
      const variant = variants.find(v => String(v.id) === String(data.variantId));
      if (!variant) return;
      const colorValue = variant[`option${optionPos}`];
      if (colorValue) {
        const qty = Math.max(1, parseInt(data.quantity, 10) || 1);
        colorCounts.set(colorValue, (colorCounts.get(colorValue) || 0) + qty);
      }
    });
    
    const hasAnyColor = colorCounts.size > 0;
    metaRowsContainer.style.display = hasAnyColor ? '' : 'none';
    
    if (hasAnyColor) {
      metaRowsContainer.innerHTML = '';
      colorCounts.forEach((count, colorValue) => {
        const row = document.createElement('div');
        row.className = 'cpc__meta-row';
        row.innerHTML = `
          <span class="cpc__color-name">${colorValue}</span>
          <span class="cpc__qty-value">${CPC_QTY_MULT}${count}</span>
        `;
        metaRowsContainer.appendChild(row);
      });
    }
  };

  const updatePreview = (state) => {
    const previewGrid = document.querySelector('[data-preview-grid]');
    if (!previewGrid) return;

    const selected = state.get();
    const existing = new Map();
    previewGrid.querySelectorAll('.preview-item').forEach(item => {
      if (item.dataset.lineId) existing.set(item.dataset.lineId, item);
    });

    const sortedEntries = Array.from(selected.entries()).sort((a, b) => {
      const slotA = a[1].slotIndex ?? Infinity;
      const slotB = b[1].slotIndex ?? Infinity;
      return slotA - slotB;
    });

    const fragment = document.createDocumentFragment();
    const itemsToKeep = new Set();

    sortedEntries.forEach(([lineId, data]) => {
      const html = data.image ? `<img src="${data.image}" alt="${data.title}" class="preview-item__image" loading="lazy">` : '';

      let item = existing.get(lineId);
      if (item) {
        item.innerHTML = html;
        itemsToKeep.add(lineId);
      } else {
        item = document.createElement('div');
        item.className = 'preview-item';
        item.dataset.lineId = lineId;
        item.dataset.productId = data.productId;
        item.innerHTML = html;
        itemsToKeep.add(lineId);
      }
      fragment.appendChild(item);
    });

    previewGrid.innerHTML = '';
    previewGrid.appendChild(fragment);

    existing.forEach((item, lineId) => {
      if (!itemsToKeep.has(lineId)) item.remove();
    });

    const openBubble = document.querySelector('[data-preview-bubble]');
    if (openBubble?.dataset.lineId) {
      const anchor = Array.from(previewGrid.querySelectorAll('.preview-item')).find(
        (el) => el.dataset.lineId === openBubble.dataset.lineId
      );
      if (anchor) {
        openBubble._anchor = anchor;
        positionPreviewBubble(openBubble, anchor);
      } else {
        removePreviewBubble();
      }
    }
  };

  const updateTotal = (state) => {
    const total = Array.from(state.get().values()).reduce((sum, { price }) => sum + price, 0);
    document.querySelectorAll('[data-total-price]').forEach(el => { el.textContent = formatMoney(total); });
  };

  const countStateItems = (state) => {
    let total = 0;
    state.get().forEach((data) => {
      total += Math.max(1, parseInt(data.quantity, 10) || 1);
    });
    return total;
  };

  const updateBottomSummary = (state) => {
    if (window.CUSTOMIZATION_3D_SHOW_BOTTOM_PRICE === true) return;

    const entries = Array.from(state.get().values());
    const itemCount = countStateItems(state);
    const single = entries.find((data) => data?.group === 'single');
    const primaryTitle = single?.title || entries[0]?.title || '';

    let label;
    let value;

    if (!entries.length) {
      label = c3dT('bottom_summary_start_label');
      value = c3dT('select_pergola');
    } else if (itemCount > 1) {
      label = c3dT('bottom_summary_items', { count: itemCount });
      value = primaryTitle || c3dT('bottom_summary_ready');
    } else {
      label = c3dT('bottom_summary_price_hint');
      value = primaryTitle || c3dT('bottom_summary_ready');
    }

    document.querySelectorAll('[data-c3d-summary-label]').forEach((el) => { el.textContent = label; });
    document.querySelectorAll('[data-c3d-summary-value]').forEach((el) => { el.textContent = value; });
  };

  const applyCpcSwatchInventoryState = (wrapper, state) => {
    const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
    if (!swatchList) return;
    const optionPos = parseInt(swatchList.dataset.optionPosition, 10);
    if (Number.isNaN(optionPos)) return;

    wrapper.querySelectorAll('[data-cpc-swatch-input]').forEach((input) => {
      const label = wrapper.querySelector(`label[for="${input.id}"]`);
      const native = input.dataset.cpcNativeAvailable === 'true';
      const disableStock = native && !colorAllowsNewAdd(state, wrapper, optionPos, input.value);
      const shouldDisable = !native || disableStock;

      if (shouldDisable) {
        input.disabled = true;
        if (native && disableStock) input.setAttribute('data-cpc-disabled-by-inventory', '');
        if (label) {
          label.classList.add('cpc__swatch--disabled');
          if (native && disableStock && !label.querySelector('.cpc__swatch-overlay')) {
            const ov = document.createElement('span');
            ov.className = 'cpc__swatch-overlay';
            ov.setAttribute('aria-hidden', 'true');
            label.appendChild(ov);
          }
        }
      } else {
        input.disabled = false;
        input.removeAttribute('data-cpc-disabled-by-inventory');
        if (label) {
          label.classList.remove('cpc__swatch--disabled');
          if (native) label.querySelector('.cpc__swatch-overlay')?.remove();
        }
      }
    });
  };

  const syncProductCards = (state) => {
    document.querySelectorAll('.product-card-clickable').forEach((wrapper) => {
      applyCpcSwatchInventoryState(wrapper, state);
      const isPendingPlacement = wrapper.classList.contains('product-pending-placement');
      wrapper.classList.toggle('product-selected', isPendingPlacement);
      wrapper.classList.toggle('product-card--no-addable', !canAddAnyLine(state, wrapper));
      updateCpcMetaRowVisibility(wrapper, state);
    });
  };

  const VARIANT_SELECTOR = [
    'fieldset[data-option-position]', '.product-card__swatch-list',
    '.color-swatch', '.color-swatch--sm', '.media-swatch', '.media-swatch--sm', '.block-swatch',
    '[data-option-value]', 'label[for^="swatch"]',
    'input[type="radio"][name^="option"]', 'input[type="checkbox"][name^="option"]',
    'select[name^="option"]', 'variant-selects', 'variant-radios',
    '.cpc__swatch', '[data-cpc-swatch-label]', '[data-cpc-swatch-list]',
  ].join(', ');

  const SWATCH_SELECTOR = [
    '.color-swatch', '.color-swatch--sm', '.media-swatch', '.media-swatch--sm',
    '.block-swatch', '[data-option-value]', 'label[for^="swatch"]',
    '.cpc__swatch', '[data-cpc-swatch-label]',
  ].join(', ');

  const executeCardAdd = async (state, wrapper, preferredVariantId = null) => {
    const { productId } = wrapper.dataset;
    const variants = getVariantData(wrapper);
    if (!variants?.length) return;

    const selectionGroup = wrapper.dataset.selectionGroup || 'multi';

    if (selectionGroup !== 'single' && !canPlaceAccessory(state)) {
      notifySelectPergolaFirst();
      return;
    }

    if (!canAddAnyLine(state, wrapper)) {
      showNotification(c3dT('no_available_inventory'), 'warning');
      return;
    }

    const variantId = preferredVariantId || getCurrentVariantId(wrapper, state) || variants[0].id;
    const variant = getVariantById(wrapper, variantId);
    if (!canUseVariantOnCard(state, wrapper, variant.id)) {
      showNotification(c3dT('color_inventory_insufficient'), 'warning');
      return;
    }

    const mySeq = ++loadOpSeq;

    if (selectionGroup === 'single') {
      const existingSingle = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single');
      const sameMainProduct = existingSingle
        && String(existingSingle[1]?.productId) === String(productId);
      const switchingMainProduct = !sameMainProduct;

      if (switchingMainProduct) {
        await clearAccessorySelections(state);
        if (loadOpSeq !== mySeq) return;
      }

      if (sameMainProduct) {
        if (String(existingSingle[1]?.variantId) !== String(variant.id)) {
          const lineId = String(existingSingle[0]);
          const applied = await applyVariantChangeToExistingModel(state, lineId, wrapper, variant);
          if (loadOpSeq !== mySeq) return;
          if (applied) return;
        } else {
          const placementResult = await queuePreparePlacementBySku(wrapper, variant);
          if (loadOpSeq !== mySeq) return;
          if (placementResult?.mode === 'placement_zone') return;
          if (placementResult?.ok) return;
          return;
        }
      }
      const existingSingles = [];
      state.get().forEach((data, id) => {
        if (data.group === 'single') existingSingles.push(id);
      });
      existingSingles.forEach((id) => state.remove(id));
    }

    const placementResult = await queuePreparePlacementBySku(wrapper, variant);
    if (loadOpSeq !== mySeq) return;
    if (!placementResult) return;
    if (placementResult.mode === 'placement_zone') return;

    const placedModelId = Array.isArray(placementResult.modelIds) ? placementResult.modelIds[0] : '';
    addProductSelectionFromWrapper(state, wrapper, variant.id, 1, placedModelId);
  };

  const handleProductCardClick = (state) => async (e) => {
    if (e.target.closest('.secondary-menu-btn, .filter-group__title, .filter-group__options')) return;

    const wrapper = e.target.closest('.product-card-clickable');
    if (!wrapper) return;

    const variants = getVariantData(wrapper);
    const { productId } = wrapper.dataset;
    if (!variants?.length) {
      console.warn('[Customization] No variant data for product:', productId);
      return;
    }

    applyCpcSwatchInventoryState(wrapper, state);

    const swatchLabel = e.target.closest('[data-cpc-swatch-label]');
    if (swatchLabel) {
      let preferredVariantId = null;
      const forAttr = swatchLabel.getAttribute('for');
      const linkedInput = forAttr ? wrapper.querySelector(`#${CSS.escape(forAttr)}`) : null;
      if (linkedInput) {
        const variants = getVariantData(wrapper);
        if (variants?.length) {
          const optionPos = parseInt(linkedInput.dataset.optionPosition, 10);
          const val = String(linkedInput.value || '').trim();
          let matched = null;
          if (!Number.isNaN(optionPos)) {
            const key = `option${optionPos}`;
            matched = variants.find((v) => String(v[key] || '').trim() === val);
          }
          if (!matched) matched = variants.find((v) => String(v.id) === String(linkedInput.dataset.variantId || ''));
          if (matched) {
            preferredVariantId = matched.id;
            wrapper.dataset.variantId = matched.id;
          }
        }
      }
      await executeCardAdd(state, wrapper, preferredVariantId);
      return;
    }

    if (e.target.closest('variant-selects, variant-radios, select, input[type="radio"], input[type="checkbox"]')) {
      const seqAtSchedule = loadOpSeq;
      setTimeout(() => {
        if (loadOpSeq !== seqAtSchedule) return;
        const variantId = getCurrentVariantId(wrapper, state);
        if (variantId) handleVariantChange(state)(wrapper, variantId);
      }, 60);
      return;
    }

    const link = e.target.closest('a');
    if (link && !link.closest('.product-card__swatch-list, fieldset[data-option-position]')) {
      e.preventDefault();
      e.stopPropagation();
    }

    await executeCardAdd(state, wrapper);
  };

  const handleVariantChange = (state) => async (wrapper, variantId) => {
    if (!wrapper || !variantId) return;

    const { productId } = wrapper.dataset;
    const variant = getVariantById(wrapper, variantId);
    if (!variant) return;

    if (window.CUSTOMIZATION_3D_LOG_VARIANT_SKU === true) {
      console.info('[C3D] variant picked', {
        productId: String(productId || ''),
        variantId: String(variantId || ''),
        sku: variant.sku != null ? String(variant.sku) : '',
      });
    }

    wrapper.dataset.variantId = variantId;
    wrapper.dataset.variantPrice = variant.price;

    updateCpcMainImage(wrapper, variant);

    if (pending3dPlacement && pending3dPlacement.wrapper === wrapper) {
      pending3dPlacement.variantId = variantId;
      pending3dPlacement.variant = variant;
    }

    const selectionGroup = wrapper.dataset.selectionGroup || 'multi';
    if (selectionGroup === 'single' && hasProductSelected(state, productId)) {
      const singleLine = Array.from(state.get().entries()).find(
        ([, data]) => data?.group === 'single' && String(data?.productId) === String(productId)
      ) || null;
      if (singleLine) {
        const lineId = String(singleLine[0]);
        await applyVariantChangeToExistingModel(state, lineId, wrapper, variant);
      }
    }
  };

  const setupVariantListeners = (state) => {
    const wrapper3D = document.querySelector('.customization-3d-wrapper');
    if (!wrapper3D) return;

    wrapper3D.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      const wrapper = e.target.closest('.product-card-clickable');
      if (!wrapper || !wrapper3D.contains(wrapper)) return;

      const variantsLocal = getVariantData(wrapper);
      if (variantsLocal?.length) {
        const radio = e.target;
        const optionPos = parseInt(radio.dataset.optionPosition, 10);
        if (!Number.isNaN(optionPos)) {
          const key = `option${optionPos}`;
          const val = String(radio.value || '').trim();
          const match = variantsLocal.find((v) => String(v[key] || '').trim() === val);
          if (match) wrapper.dataset.variantId = match.id;
        } else if (radio.dataset.variantId) {
          wrapper.dataset.variantId = radio.dataset.variantId;
        }
      }

      const seqAtSchedule = loadOpSeq;
      setTimeout(() => {
        if (loadOpSeq !== seqAtSchedule) return;
        const variantId = getCurrentVariantId(wrapper, state);
        if (variantId) handleVariantChange(state)(wrapper, variantId);
      }, 60);
    });
  };

  const preventDefaultClicks = () => {
    document.addEventListener('click', (e) => {
      if (e.target.closest(VARIANT_SELECTOR)) return;

      const inWrapper = (el) => el?.closest('.customization-3d-wrapper');

      const vendorLink = e.target.closest('a[href*="/collections/vendors"]');
      if (vendorLink && inWrapper(vendorLink)) { e.preventDefault(); e.stopPropagation(); return; }

      const productLink = e.target.closest('a[href*="/products/"]');
      if (productLink && inWrapper(productLink) && !productLink.closest('.product-card__swatch-list')) {
        e.preventDefault(); e.stopPropagation(); return;
      }

      const quickBuy = e.target.closest('.quick-add, [data-quick-add], .product-card__quick-buy, .product-form__submit');
      if (quickBuy && inWrapper(quickBuy)) { e.preventDefault(); e.stopPropagation(); }
    });
  };

  const handlePrimaryClick = (state) => async (e) => {
    if (!e.target.closest('.customization-3d-wrapper .primary-menu')) return;
    const btn = e.target.closest('.primary-menu-item');
    if (!btn) return;

    await activatePrimaryPanelForHotspot(btn.dataset.menuId, state, { clearHotspotFilter: true });
    clearPending3dPlacement();
    getVendor()?.hidePlacementZones?.();
  };

  const resolveHotspotAccessoryPanelId = (filterSet) => {
    if (!filterSet || !filterSet.size) return null;
    let bestId = null;
    let bestCount = 0;
    document.querySelectorAll('.panel-content[data-selection-group="multi"]').forEach((panel) => {
      let count = 0;
      panel.querySelectorAll('.product-card-clickable').forEach((card) => {
        if (cardMatchesHotspotSkuFilter(card, filterSet)) count += 1;
      });
      if (count > bestCount) {
        bestCount = count;
        bestId = panel.dataset.panelId;
      }
    });
    if (bestId) return bestId;
    const fallback = document.querySelector('.primary-menu-item[data-selection-group="multi"]');
    return fallback?.dataset?.menuId || null;
  };

  const resetPanelProductsScroll = (panel) => {
    const root = document.querySelector('.customization-3d-wrapper');
    if (!root) return;
    const candidates = [
      panel?.querySelector('.products-wrapper'),
      panel?.closest('.secondary-panel-content'),
      root.querySelector('.secondary-panel-content'),
    ].filter(Boolean);
    const seen = new Set();
    candidates.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      el.scrollTop = 0;
    });
  };

  const activatePrimaryPanelForHotspot = async (panelId, state, options = {}) => {
    await ensurePanelProductsLoaded(panelId);
    const targetPanel = document.querySelector(`.panel-content[data-panel-id="${panelId}"]`);
    if (!targetPanel || !state) return;
    const menuBtn = document.querySelector(`.primary-menu-item[data-menu-id="${panelId}"]`);
    document.querySelectorAll('.primary-menu-item').forEach((item) => item.classList.remove('active'));
    if (menuBtn) menuBtn.classList.add('active');
    document.querySelectorAll('.panel-content').forEach((panel) => panel.classList.remove('active'));
    targetPanel.classList.add('active');
    resetPanelProductsScroll(targetPanel);
    syncMobileFilterExpandedForPanel(targetPanel);
    resetSecondaryFilterSelection(targetPanel);
    filterProducts(targetPanel, state);
    syncProductCards(state);
    if (options.clearHotspotFilter) clearHotspotSkuFilter();
    else if (activeHotspotSkuFilter) applyHotspotSkuFilter([...activeHotspotSkuFilter]);
    updateMobileFilterBackdrop();
  };

  const resetSecondaryFilterSelection = (panel) => {
    if (!panel) return;
    panel.querySelectorAll('[data-filter-group] .secondary-menu-btn.active').forEach((btn) => {
      btn.classList.remove('active');
    });
  };

  const normalizeSizeToken = (value) => {
    const raw = String(value ?? '').trim().toLowerCase();
    const num = raw.match(/(\d+(?:\.\d+)?)/);
    return num ? num[1] : raw.replace(/\s+/g, '');
  };

  const parsePergolaLegsFromSingle = (sizeValue) => {
    if (sizeValue == null || sizeValue === '') return [];
    const s = String(sizeValue).trim();
    const cross = s.match(/(\d+(?:\.\d+)?)\s*['′"]?\s*[x×X]\s*(\d+(?:\.\d+)?)\s*['′"]?/i);
    if (cross) {
      const a = normalizeSizeToken(cross[1]);
      const b = normalizeSizeToken(cross[2]);
      if (a && b) return a === b ? [a] : [a, b];
    }
    const single = s.match(/^(\d+(?:\.\d+)?)\s*['′"]?$/);
    if (single) {
      const leg = normalizeSizeToken(single[1]);
      return leg ? [leg] : [];
    }
    return [];
  };

  const parsePergolaLegsFromSizeValues = (values) => {
    if (!Array.isArray(values) || !values.length) return [];
    const legs = new Set();
    for (const v of values) {
      for (const leg of parsePergolaLegsFromSingle(v)) legs.add(leg);
    }
    return [...legs];
  };

  const parseC3dCompatibleSizeOverride = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return [];
    return parsePergolaLegsFromSizeValues(splitMetafieldValues(s));
  };

  const parseProductMetafieldsRaw = (raw) => {
    const metafields = new Map();
    if (!raw) return metafields;

    const segments = String(raw).includes('||')
      ? String(raw).split('||')
      : [String(raw)];

    segments.forEach((segment) => {
      let currentKey = null;
      let currentValues = [];

      segment.split(',').forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;

        const doubleColon = trimmed.indexOf('::');
        if (doubleColon > 0) {
          if (currentKey && currentValues.length) {
            metafields.set(currentKey, currentValues.join(','));
          }
          currentKey = trimmed.substring(0, doubleColon).trim();
          const val = trimmed.substring(doubleColon + 2).trim();
          currentValues = val ? [val] : [];
          return;
        }

        const colonIdx = trimmed.indexOf(':');
        if (colonIdx > 0) {
          if (currentKey && currentValues.length) {
            metafields.set(currentKey, currentValues.join(','));
          }
          currentKey = trimmed.substring(0, colonIdx).trim();
          const val = trimmed.substring(colonIdx + 1).trim();
          currentValues = val ? [val] : [];
        } else if (currentKey && trimmed) {
          currentValues.push(trimmed);
        }
      });

      if (currentKey && currentValues.length) {
        metafields.set(currentKey, currentValues.join(','));
      }
    });

    return metafields;
  };

  const splitMetafieldValues = (rawValue) =>
    String(rawValue ?? '')
      .replace(/[\[\]"]/g, '')
      .trim()
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const getMetafieldValues = (metafields, key) => {
    if (!metafields || !key) return [];
    const target = String(key).toLowerCase();
    for (const [k, rawValue] of metafields) {
      if (String(k).toLowerCase() === target) return splitMetafieldValues(rawValue);
    }
    return [];
  };

  const getMetafieldValuesForKeys = (metafields, keys) => {
    for (const key of keys) {
      const vals = getMetafieldValues(metafields, key);
      if (vals.length) return vals;
    }
    return [];
  };

  const C3D_COMPATIBLE_SIZE_MF_KEYS = [
    'custom_3d_compatible_size',
    '3d_compatible_size',
    '3d-compatible-size',
  ];

  const getC3dCompatibleSizeFromCatalog = (productId, handle) => {
    const pid = productId != null ? String(productId).trim() : '';
    const h = handle != null ? String(handle).trim() : '';
    if (!pid && !h) return '';
    const index = getCatalogIndex();
    let row = null;
    if (pid) row = index.find((r) => String(r.id) === pid);
    if (!row && h) row = index.find((r) => r.h === h);
    const raw = row?.c3d ?? row?.c3dCompatibleSize ?? '';
    return String(raw || '').trim();
  };

  const getC3dCompatibleSizeFromWrapper = (wrapper) => {
    if (!wrapper) return '';
    const metaScript = wrapper.querySelector('[data-c3d-product-meta]');
    if (metaScript) {
      try {
        const meta = JSON.parse(metaScript.textContent);
        const fromMeta = String(meta?.c3dCompatibleSize ?? '').trim();
        if (fromMeta) return fromMeta;
      } catch (_) {
        /* ignore */
      }
    }
    const fromAttr = wrapper.getAttribute('data-c3d-compatible-size');
    if (fromAttr && String(fromAttr).trim()) return String(fromAttr).trim();
    const metafields = parseProductMetafieldsRaw(wrapper.dataset.productMetafields || '');
    for (const key of C3D_COMPATIBLE_SIZE_MF_KEYS) {
      const vals = getMetafieldValues(metafields, key);
      if (vals[0]) return String(vals[0]).trim();
    }
    const fromCatalog = getC3dCompatibleSizeFromCatalog(
      wrapper.dataset.productId,
      wrapper.dataset.productHandle
    );
    if (fromCatalog) return fromCatalog;
    return '';
  };

  const hasExplicitC3dCompatibleSize = (wrapper, entry) => {
    if (wrapper?.dataset?.c3dHasCompatibleSize === 'true') return true;
    if (String(entry?.c3dCompatibleSize || '').trim()) return true;
    if (getC3dCompatibleSizeFromWrapper(wrapper)) return true;
    const fromCatalog = getC3dCompatibleSizeFromCatalog(entry?.productId, entry?.handle);
    return Boolean(fromCatalog);
  };

  const resolveAccessoryLegsFromPergolaSku = (sku, handle) => {
    const s = String(sku || '').trim().toUpperCase();
    const h = String(handle || '').trim().toLowerCase();
    if (!s && !h) return [];
    const only10_20 = /(?=.*10)(?=.*20)/.test(s) || /10\s*['′"]?\s*[x×]\s*20|1020/.test(h);
    if (!only10_20) return [];
    if (s === 'SPF111S1020PILLAR4PCS') return ['10', '13'];
    if (h.includes('viewscape') && /1020|10x20|10-x-20/.test(h)) return ['10', '13'];
    return [];
  };

  const resolvePergolaAccessoryFilterLegs = (entry, wrapper) => {
    const rawSources = [];
    if (entry?.c3dCompatibleSize) rawSources.push(String(entry.c3dCompatibleSize).trim());
    if (wrapper) rawSources.push(getC3dCompatibleSizeFromWrapper(wrapper));
    rawSources.push(getC3dCompatibleSizeFromCatalog(entry?.productId, entry?.handle));

    const seenRaw = new Set();
    const explicit = hasExplicitC3dCompatibleSize(wrapper, entry);

    for (const raw of rawSources) {
      const s = String(raw || '').trim();
      if (!s || seenRaw.has(s)) continue;
      seenRaw.add(s);
      const legs = parseC3dCompatibleSizeOverride(s);
      if (legs.length) return legs;
      const singleLegs = parsePergolaLegsFromSingle(s);
      if (singleLegs.length) return singleLegs;
    }

    if (explicit) return [];

    if (wrapper && entry?.variantId) {
      const skuLegs = resolveAccessoryLegsFromPergolaSku(
        getVariantById(wrapper, entry.variantId)?.sku,
        wrapper.dataset.productHandle
      );
      if (skuLegs.length) return skuLegs;
    }

    if (!wrapper) return [];

    const metafields = parseProductMetafieldsRaw(wrapper.dataset.productMetafields || '');
    const mainPanel = wrapper.closest('.panel-content');
    const panelSizeKeys = mainPanel ? getSizeFilterKeysForPanel(mainPanel) : ['Sizes', 'Size'];
    let legs = parsePergolaLegsFromSizeValues(getMetafieldValuesForKeys(metafields, panelSizeKeys));
    if (!legs.length) {
      const variantTitle = entry?.variantTitle || '';
      if (variantTitle) legs = parsePergolaLegsFromSingle(variantTitle);
    }
    return legs;
  };

  const isSizeLikeFilterKey = (key) => {
    const kl = String(key || '').trim().toLowerCase();
    return kl === 'size' || kl === 'sizes';
  };

  const isCompositeSizeValue = (value) => /[x×X]/.test(String(value ?? ''));

  const normalizeCompositeSize = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/['′"]/g, '')
      .replace(/×/g, 'x');

  const extractSizeDimensionTokens = (value) => {
    const s = String(value ?? '').trim();
    if (!s) return [];
    const tokens = new Set();
    for (const m of s.matchAll(/(\d+(?:\.\d+)?)\s*['′"]/gi)) {
      if (m[1]) tokens.add(normalizeSizeToken(m[1]));
    }
    const cross = s.match(/(\d+(?:\.\d+)?)\s*['′"]?\s*[x×X]\s*(\d+(?:\.\d+)?)/i);
    if (cross) {
      const a = normalizeSizeToken(cross[1]);
      const b = normalizeSizeToken(cross[2]);
      if (a) tokens.add(a);
      if (b) tokens.add(b);
    }
    if (!tokens.size) {
      const leg = normalizeSizeToken(s);
      if (leg) tokens.add(leg);
    }
    return [...tokens];
  };

  const sizeFilterValuesMatch = (productValue, filterValue) => {
    const fv = String(filterValue ?? '').trim();
    const pv = String(productValue ?? '').trim();
    if (!fv || !pv) return false;

    if (isCompositeSizeValue(fv)) {
      const nf = normalizeCompositeSize(fv);
      const np = normalizeCompositeSize(pv);
      return np === nf || np.includes(nf);
    }

    const filterLegs = extractSizeDimensionTokens(fv);
    const productLegs = extractSizeDimensionTokens(pv);
    if (filterLegs.length && productLegs.length) {
      return filterLegs.some((fl) => productLegs.includes(fl));
    }
    return pv.toLowerCase().includes(fv.toLowerCase());
  };

  const metafieldFilterValuesMatch = (filterKey, productValue, filterValue) => {
    if (isSizeLikeFilterKey(filterKey)) {
      return sizeFilterValuesMatch(productValue, filterValue);
    }
    return String(productValue).trim().toLowerCase() === String(filterValue).trim().toLowerCase();
  };

  const getSizeFilterKeysForPanel = (panel) => {
    const keys = [];
    panel?.querySelectorAll('[data-filter-group][data-filter-key]').forEach((el) => {
      const k = el.dataset.filterKey;
      if (k && isSizeLikeFilterKey(k) && !keys.includes(k)) keys.push(k);
    });
    return keys.length ? keys : ['Sizes', 'Size'];
  };

  const extractPergolaLegsFromMetafields = (metafields) => {
    const sizeKeys = [];
    for (const [key] of metafields) {
      if (isSizeLikeFilterKey(key) && !sizeKeys.includes(key)) sizeKeys.push(key);
    }
    if (sizeKeys.length) {
      const legs = parsePergolaLegsFromSizeValues(getMetafieldValuesForKeys(metafields, sizeKeys));
      if (legs.length) return legs;
    }
    const legs = new Set();
    for (const [, rawValue] of metafields) {
      for (const leg of parsePergolaLegsFromSizeValues(splitMetafieldValues(rawValue))) legs.add(leg);
    }
    return [...legs];
  };

  const panelHasPergolaSizeFilterEnabled = (panel) =>
    panel?.dataset?.c3dPergolaSizeFilter === 'true';

  const isAccessoryProductType = (productType) =>
    /\baccessory\b/i.test(String(productType || '').trim());

  const normalizeProductLineToken = (token) => {
    const raw = String(token || '').trim();
    if (!raw) return null;
    const slash = raw.match(/^(\d+\s*\/\s*\d+)/);
    if (slash) return slash[1].replace(/\s/g, '');
    const digits = raw.match(/^(\d+)/);
    if (digits) return digits[1];
    return raw.toLowerCase();
  };

  const parseProductLineFromType = (productType) => {
    const s = String(productType || '').trim();
    if (!s) return null;
    const accessory = s.match(/^(.+?)\s+accessory$/i);
    if (accessory) return normalizeProductLineToken(accessory[1]);
    const pergola = s.match(/^(.+?)\s+pergola$/i);
    if (pergola) return normalizeProductLineToken(pergola[1]);
    return normalizeProductLineToken(s);
  };

  const cardMatchesPergolaProductLine = (card, activeLine) => {
    const productType = card?.dataset?.productType || '';
    if (!isAccessoryProductType(productType)) return true;
    if (!activeLine) return true;
    const cardLine = parseProductLineFromType(productType);
    if (!cardLine) return true;
    return cardLine === activeLine;
  };

  const cardMatchesPergolaSizeLegs = (card, legs, sizeKeys) => {
    if (!legs || !legs.length) return true;
    const metafields = parseProductMetafieldsRaw(card.dataset.productMetafields || '');
    const sizeValues = getMetafieldValuesForKeys(metafields, sizeKeys);
    if (!sizeValues.length) return true;
    return sizeValues.some((v) =>
      legs.some((leg) => sizeFilterValuesMatch(v, leg) || sizeFilterValuesMatch(v, `${leg}'`))
    );
  };

  const syncPergolaSizeFilterOptions = (panel, state) => {
    if (!panel) return;
    const restrict = panelHasPergolaSizeFilterEnabled(panel) && Boolean(activePergolaSizeLegs?.length);
    let needRefilter = false;

    panel.querySelectorAll('[data-filter-group][data-filter-key]').forEach((group) => {
      if (!isSizeLikeFilterKey(group.dataset.filterKey)) return;
      const buttons = [...group.querySelectorAll('.secondary-menu-btn[data-filter-value]')];

      buttons.forEach((btn) => {
        if (!restrict) {
          btn.style.display = '';
          return;
        }
        const match = activePergolaSizeLegs.includes(normalizeSizeToken(btn.dataset.filterValue));
        btn.style.display = match ? '' : 'none';
        if (!match && btn.classList.contains('active')) {
          btn.classList.remove('active');
          needRefilter = true;
        }
      });
    });

    if (needRefilter && state) filterProducts(panel, state);
  };

  const updatePergolaSizeLegFromState = (state) => {
    const entry = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single');
    if (!entry) {
      activePergolaSizeLegs = null;
      return;
    }

    const productId = entry[1]?.productId;
    const wrapper =
      document.querySelector(
        `.product-card-clickable[data-product-id="${productId}"][data-selection-group="single"]`
      ) || document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);

    if (wrapper && entry[1]) {
      const resolved = getC3dCompatibleSizeFromWrapper(wrapper)
        || getC3dCompatibleSizeFromCatalog(entry[1].productId, entry[1].handle);
      if (resolved) entry[1].c3dCompatibleSize = resolved;
    }

    const legs = resolvePergolaAccessoryFilterLegs(entry[1], wrapper);
    activePergolaSizeLegs = legs.length ? legs : null;

    document.querySelectorAll('.panel-content').forEach((panel) => {
      if (!panelHasPergolaSizeFilterEnabled(panel)) return;
      syncPergolaSizeFilterOptions(panel, state);
    });
  };

  const updatePergolaProductLineFromState = (state) => {
    const entry = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single');
    if (!entry) {
      activePergolaProductLine = null;
      return;
    }

    const productId = entry[1]?.productId;
    const wrapper =
      document.querySelector(
        `.product-card-clickable[data-product-id="${productId}"][data-selection-group="single"]`
      ) || document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);

    if (!wrapper) {
      activePergolaProductLine = null;
      return;
    }

    activePergolaProductLine = parseProductLineFromType(wrapper.dataset.productType || '');
  };

  const refreshAllPanelFilters = (state) => {
    document.querySelectorAll('.panel-content').forEach((panel) => {
      filterProducts(panel, state);
    });
  };

  const filterProducts = (panel, state) => {
    if (!panel) return;

    syncPergolaSizeFilterOptions(panel, state);

    const activeFilters = new Map();
    panel.querySelectorAll('.secondary-menu-btn.active').forEach(btn => {
      const { filterKey, filterValue } = btn.dataset;
      if (filterKey && filterValue) {
        if (!activeFilters.has(filterKey)) activeFilters.set(filterKey, []);
        activeFilters.get(filterKey).push(filterValue);
      }
    });

    let visibleCount = 0;

    const pergolaFilterEnabled = panelHasPergolaSizeFilterEnabled(panel);
    const pergolaLegs = pergolaFilterEnabled ? activePergolaSizeLegs : null;
    const sizeKeys = pergolaLegs?.length ? getSizeFilterKeysForPanel(panel) : [];
    panel.querySelectorAll('.product-card-clickable').forEach((card) => {
      let shouldShow = true;
      const metafields = parseProductMetafieldsRaw(card.dataset.productMetafields || '');

      if (activeFilters.size > 0) {
        for (const [filterKey, selectedValues] of activeFilters) {
          const productValues = getMetafieldValues(metafields, filterKey);
          if (!productValues.length) {
            shouldShow = false;
            break;
          }

          const matches = selectedValues.some((sel) =>
            productValues.some((v) => metafieldFilterValuesMatch(filterKey, v, sel))
          );
          if (!matches) {
            shouldShow = false;
            break;
          }
        }
      }

      if (shouldShow && pergolaLegs?.length) {
        shouldShow = cardMatchesPergolaSizeLegs(card, pergolaLegs, sizeKeys);
      }

      if (shouldShow && activePergolaProductLine) {
        shouldShow = cardMatchesPergolaProductLine(card, activePergolaProductLine);
      }

      if (shouldShow && activeHotspotSkuFilter) {
        shouldShow = cardMatchesHotspotSkuFilter(card, activeHotspotSkuFilter);
      }

      card.style.display = shouldShow ? '' : 'none';
      card.classList.toggle(
        'product-card--hotspot-filtered-out',
        Boolean(activeHotspotSkuFilter) && !cardMatchesHotspotSkuFilter(card, activeHotspotSkuFilter)
      );
      if (shouldShow) visibleCount += 1;
    });

    const container = panel.querySelector('.products-container');
    let noResults = container?.querySelector('.no-results-message');
    const hasRestrictiveFilters =
      activeFilters.size > 0 ||
      (pergolaFilterEnabled && Boolean(pergolaLegs?.length)) ||
      Boolean(activeHotspotSkuFilter);

    if (visibleCount === 0 && hasRestrictiveFilters) {
      if (!noResults) {
        noResults = document.createElement('div');
        noResults.className = 'no-results-message';
        noResults.style.cssText = 'text-align:center;padding:40px 20px;color:#999;';
        noResults.textContent = c3dT('no_results_found');
        container?.appendChild(noResults);
      }
      noResults.style.display = 'block';
    } else if (noResults) {
      noResults.style.display = 'none';
    }
  };

  const handleSecondaryClick = (state) => (e) => {
    if (!e.target.closest('.customization-3d-wrapper .secondary-panel')) return;
    const btn = e.target.closest('.secondary-menu-btn');
    if (!btn || !btn.dataset.filterKey) return;

    e.stopPropagation();
    e.preventDefault();

    const panel = btn.closest('.panel-content');
    if (!panel) return;

    btn.classList.toggle('active');
    filterProducts(panel, state);
    syncProductCards(state);
    if (activeHotspotSkuFilter) applyHotspotSkuFilter([...activeHotspotSkuFilter]);
  };

  const removePreviewBubble = () => {
    const bubble = document.querySelector('[data-preview-bubble]');
    if (bubble?._repositionPreviewBubble) {
      window.removeEventListener('scroll', bubble._repositionPreviewBubble, true);
      window.removeEventListener('resize', bubble._repositionPreviewBubble);
    }
    bubble?.remove();
  };

  const positionPreviewBubble = (bubble, anchor) => {
    const grid = anchor?.closest('[data-preview-grid]');
    if (!grid || !bubble) return;
    bubble.style.left = `${anchor.offsetLeft + anchor.offsetWidth / 2}px`;
    bubble.style.top = `${anchor.offsetTop}px`;
    bubble.style.transform = 'translate(-50%, calc(-100% - 8px))';
  };

  const getPreviewSwatchCandidates = (wrapper, state, lineId) => {
    const swatchList = wrapper?.querySelector('[data-cpc-swatch-list]');
    const optionPos = swatchList?.dataset.optionPosition;
    if (!optionPos) return { optionPos: null, items: [] };
    const data = state?.get?.()?.get(lineId);
    const currentVariantId = data?.variantId;
    const items = Array.from(wrapper.querySelectorAll('[data-cpc-swatch-input]')).map((input) => {
      const label = wrapper.querySelector(`label[for="${input.id}"]`);
      const native = input.dataset.cpcNativeAvailable === 'true';
      const picked = native && state && currentVariantId != null
        ? pickVariantForColor(wrapper, currentVariantId, optionPos, input.value, state, lineId)
        : null;
      const disabled = !native || (state && currentVariantId != null ? !picked : !!input.disabled);
      return {
        value: input.value,
        disabled,
        style: label?.getAttribute('style') || '',
        extraClass: label?.className || '',
      };
    });
    return { optionPos, items };
  };

  const pickVariantForColor = (wrapper, currentVariantId, optionPos, colorValue, state = null, lineId = null) => {
    const variants = getVariantData(wrapper) || [];
    const current = getVariantById(wrapper, currentVariantId) || variants[0];
    if (!current) return null;
    const key = `option${optionPos}`;
    const otherKeys = ['option1', 'option2', 'option3'].filter((k) => k !== key);

    const passesInventory = (v) => {
      if (!v || v.available === false) return false;
      if (!state || !wrapper) return true;
      if (lineId != null) return canAccommodateLineSwitch(state, wrapper, v.id, lineId);
      return canAccommodateNewLine(state, wrapper, v.id);
    };

    const exact = variants.find((v) => {
      if (toStr(v[key]) !== toStr(colorValue)) return false;
      return otherKeys.every((k) => toStr(v[k]) === toStr(current[k]));
    });
    if (exact && passesInventory(exact)) return exact;

    const sameColor = variants.filter((v) => toStr(v[key]) === toStr(colorValue));
    return sameColor.find((v) => passesInventory(v)) || null;
  };

  const parseHexFromCssBackgroundStyle = (styleStr) => {
    const m = String(styleStr || '').match(/#[0-9a-f]{6}\b|#[0-9a-f]{3}\b/i);
    return m ? m[0] : '';
  };

  const getVariantSwatchHex = (wrapper, variantId) => {
    if (!wrapper || !variantId) return '';
    const variant = getVariantById(wrapper, variantId);
    if (!variant) return '';
    const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
    const optionPos = swatchList?.dataset.optionPosition;
    if (!optionPos) return '';
    const colorValue = variant[`option${optionPos}`];
    if (colorValue == null) return '';
    const target = String(colorValue).trim().toLowerCase();
    const input = Array.from(wrapper.querySelectorAll('[data-cpc-swatch-input]')).find(
      (el) => String(el.value || '').trim().toLowerCase() === target
    );
    if (!input) return '';
    const label = wrapper.querySelector(`label[for="${input.id}"]`);
    const byStyle = parseHexFromCssBackgroundStyle(label?.getAttribute('style') || '');
    if (byStyle) return byStyle;
    if (label?.classList?.contains('cpc__swatch--white')) return '#FFFFFF';
    try {
      const bg = window.getComputedStyle(label).backgroundColor;
      const m = bg && bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
      if (m) {
        const r = parseInt(m[1], 10);
        const g = parseInt(m[2], 10);
        const b = parseInt(m[3], 10);
        return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
      }
    } catch (_) {
    }
    return '';
  };

  const syncModelMaterialColorFromVariant = (wrapper, variantId) => {
    const hex = getVariantSwatchHex(wrapper, variantId);
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return;
    const viewer = get3DViewer();
    const mat = String(window.CUSTOMIZATION_3D_LAST_MATERIAL_NAME || 'zhuomian').trim();
    viewer?.material?.color?.(mat, hex);
  };

  const applyVariantChangeToExistingModel = async (state, lineId, wrapper, picked) => {
    const old = state?.get?.()?.get(lineId);
    if (!old || !wrapper || !picked) return false;
    const next = {
      ...old,
      lineId,
      productId: old.productId || wrapper.dataset.productId,
      variantId: picked.id,
      price: picked.price || parseFloat(wrapper.dataset.variantPrice) || old.price || 0,
      variantTitle: picked.title || '',
      options: getOptionsFromVariant(picked),
      image: picked.image || old.image || wrapper.dataset.productImage || '',
      quantity: Math.max(1, parseInt(old.quantity, 10) || 1),
    };
    const group = String(old.group || wrapper.dataset.selectionGroup || '').trim();
    const modelKey = String(old.modelKey || lineId || '').trim();
    const sku = String(picked.sku || '').trim();
    state.add(lineId, next);
    let applied = false;
    const vendor = getVendor();
    if (sku && vendor?.applySkuToModel) {
      const result = await vendor.applySkuToModel(sku, modelKey, {
        colorOnly: true,
        replaceScene: group === 'single',
      });
      applied = !!result?.ok;
      if (applied && group === 'single') vendor?.setPergolaSku?.(sku);
    }
    if (!applied && group === 'single') {
      syncModelMaterialColorFromVariant(wrapper, picked.id);
      applied = true;
    }
    if (!applied && group !== 'single') return false;
    return true;
  };

  const fillElToHex = (fillEl) => {
    if (!fillEl) return '';
    try {
      const ctx = String(fillEl.getAttribute('style') || '');
      const fromAttr = parseHexFromCssBackgroundStyle(
        ctx.replace(/--swatch-background:\s*/gi, ' ')
      );
      if (fromAttr) return fromAttr;
    } catch (_) {
      /* ignore */
    }
    try {
      const cs = window.getComputedStyle(fillEl);
      if (cs.backgroundImage && cs.backgroundImage !== 'none') return '';
      const bg = cs.backgroundColor;
      const m = bg && bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
      if (!m) return '';
      const r = parseInt(m[1], 10);
      const g = parseInt(m[2], 10);
      const b = parseInt(m[3], 10);
      return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
    } catch (_) {
      return '';
    }
  };

  const clearModelActionBubbleSwatches = (bubble) => {
    const root = bubble.querySelector('.preview-bubble__swatches');
    if (root) root.innerHTML = '';
  };

  const modelBubbleHasColorSwatches = (wrapper, state, lineId) => {
    if (!wrapper || lineId == null || lineId === '') return false;
    const { items } = getPreviewSwatchCandidates(wrapper, state, lineId);
    return items.length > 0;
  };

  const fillModelActionBubbleSwatches = (bubble, wrapper, data, items, optionPos) => {
    const swatchRoot = bubble.querySelector('.preview-bubble__swatches');
    if (!swatchRoot || !wrapper || !optionPos) return;
    if (!items.length) {
      clearModelActionBubbleSwatches(bubble);
      return;
    }
    swatchRoot.innerHTML = '';
    const currentVariant = getVariantById(wrapper, data?.variantId);
    const selectedColor = currentVariant ? currentVariant[`option${optionPos}`] : '';
    items.forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'preview-bubble__swatch';
      if (item.disabled) btn.classList.add('preview-bubble__swatch--disabled');
      if (item.extraClass && item.extraClass.includes('cpc__swatch--white')) {
        btn.classList.add('preview-bubble__swatch--white');
      }
      if (!item.disabled && toStr(item.value) === toStr(selectedColor)) btn.classList.add('preview-bubble__swatch--selected');
      if (item.disabled) btn.setAttribute('disabled', '');
      btn.setAttribute('aria-label', String(item.value || ''));
      const fill = document.createElement('span');
      fill.className = 'preview-bubble__swatch-fill';
      if (item.style) fill.style.cssText = item.style.replace(/--swatch-background:/g, 'background:');
      btn.appendChild(fill);
      if (item.disabled) {
        const overlay = document.createElement('span');
        overlay.className = 'preview-bubble__swatch-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        btn.appendChild(overlay);
      }
      const preHex = parseHexFromCssBackgroundStyle(item.style || '');
      btn.dataset.modelBubbleColor = preHex || '';
      btn.dataset.modelBubbleOptionValue = String(item.value || '');
      swatchRoot.appendChild(btn);
    });
    const hasSelected = !!swatchRoot.querySelector('.preview-bubble__swatch--selected:not(.preview-bubble__swatch--disabled)');
    if (!hasSelected) {
      const firstEnabled = swatchRoot.querySelector('.preview-bubble__swatch:not(.preview-bubble__swatch--disabled)');
      if (firstEnabled) firstEnabled.classList.add('preview-bubble__swatch--selected');
    }
    requestAnimationFrame(() => {
      swatchRoot.querySelectorAll('.preview-bubble__swatch').forEach((btn) => {
        const cur = String(btn.dataset.modelBubbleColor || '').trim();
        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(cur)) return;
        const fill = btn.querySelector('.preview-bubble__swatch-fill');
        const h = fillElToHex(fill);
        if (h) btn.dataset.modelBubbleColor = h;
        else if (!String(btn.dataset.modelBubbleColor || '').trim()) btn.dataset.modelBubbleColor = '#666666';
      });
    });
  };

  const resolveModelBubbleLineContext = (state, preferredAttachKey = '') => {
    if (!state) return { lineId: null, productId: null };
    const attachKey = String(preferredAttachKey || '').trim();

    if (attachKey && state.get().has(attachKey)) {
      const d = state.get().get(attachKey);
      return { lineId: attachKey, productId: String(d?.productId || '') || null };
    }

    const sorted = Array.from(state.get().entries()).sort(
      (a, b) => (a[1].slotIndex ?? Infinity) - (b[1].slotIndex ?? Infinity)
    );

    if (attachKey === SINGLE_MODEL_KEY) {
      const hit = sorted.find(([, data]) => data?.group === 'single');
      if (hit) return { lineId: String(hit[0]), productId: String(hit[1]?.productId || '') || null };
    }

    const byProductId = /^product-(\d+)$/.exec(attachKey)?.[1] || null;
    if (byProductId) {
      const hit = sorted.find(([, data]) => String(data?.productId) === String(byProductId));
      if (hit) return { lineId: String(hit[0]), productId: String(byProductId) };
    }

    const byModelKey = attachKey
      ? sorted.find(([, data]) => String(data?.modelKey || '') === attachKey)
      : null;
    if (byModelKey) {
      return { lineId: String(byModelKey[0]), productId: String(byModelKey[1]?.productId || '') || null };
    }

    const singles = sorted.filter(([, data]) => data?.group === 'single');
    const multiModelKeys = new Set(
      sorted
        .filter(([, data]) => data?.group !== 'single')
        .map(([, data]) => String(data?.modelKey || '').trim())
        .filter(Boolean)
    );
    const isAccessoryAttachKey = multiModelKeys.has(attachKey) || /_\d{10,}$/.test(attachKey);
    if (singles.length === 1 && !isAccessoryAttachKey) {
      const hit = singles[0];
      return { lineId: String(hit[0]), productId: String(hit[1]?.productId || '') || null };
    }

    if (!attachKey) {
      if (singles.length === 1) {
        const hit = singles[0];
        return { lineId: String(hit[0]), productId: String(hit[1]?.productId || '') || null };
      }
      const host = document.querySelector('[data-3d-container]');
      const primaryLineId = String(host?.dataset?.c3dPrimaryLineId || '').trim();
      const primaryProductId = String(host?.dataset?.c3dPrimaryProductId || '').trim();
      if (primaryLineId && state.get().has(primaryLineId)) {
        return { lineId: primaryLineId, productId: primaryProductId || null };
      }
    }

    return { lineId: null, productId: null };
  };

  const syncModelActionBubbleSwatchesFromPrimary = (bubble) => {
    const host = document.querySelector('[data-3d-container]');
    const st = host && host.__c3dState;
    const viewer = get3DViewer();
    const pickAttachKey = String(
      lastModelPickForBubble?.attachKey
      || viewer?.resolveAttachKeyFromPick?.(lastModelPickForBubble || null)
      || ''
    ).trim();
    let ctx = resolveModelBubbleLineContext(st, pickAttachKey);
    if ((!ctx.lineId || !ctx.productId) && st) {
      const singles = Array.from(st.get().entries()).filter(([, data]) => data?.group === 'single');
      if (singles.length === 1) {
        ctx = {
          lineId: String(singles[0][0]),
          productId: String(singles[0][1]?.productId || '') || null,
        };
      }
    }
    const lineId = ctx.lineId;
    const productId = ctx.productId;
    const wrapper = productId
      ? document.querySelector(`.product-card-clickable[data-product-id="${String(productId)}"]`)
      : null;
    if (!wrapper || !st || lineId == null || lineId === '') {
      clearModelActionBubbleSwatches(bubble);
      return false;
    }
    const lineData = st.get().get(lineId);
    if (!lineData) {
      clearModelActionBubbleSwatches(bubble);
      return false;
    }
    const { optionPos, items } = getPreviewSwatchCandidates(wrapper, st, lineId);
    if (!optionPos || !items.length) {
      clearModelActionBubbleSwatches(bubble);
      return false;
    }
    fillModelActionBubbleSwatches(bubble, wrapper, lineData, items, optionPos);
    return true;
  };

  const fillPreviewBubbleSwatches = (bubble, wrapper, data, items, optionPos) => {
    const swatchRoot = bubble.querySelector('.preview-bubble__swatches');
    if (!swatchRoot || !wrapper || !optionPos) return;
    if (!items.length) {
      swatchRoot.innerHTML = '';
      return;
    }
    swatchRoot.innerHTML = '';
    const currentVariant = getVariantById(wrapper, data.variantId);
    const selectedColor = currentVariant ? currentVariant[`option${optionPos}`] : '';
    items.forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'preview-bubble__swatch';
      if (item.disabled) btn.classList.add('preview-bubble__swatch--disabled');
      if (item.extraClass && item.extraClass.includes('cpc__swatch--white')) btn.classList.add('preview-bubble__swatch--white');
      if (!item.disabled && toStr(item.value) === toStr(selectedColor)) btn.classList.add('preview-bubble__swatch--selected');
      btn.setAttribute('data-preview-bubble-color', item.value);
      if (item.disabled) btn.setAttribute('disabled', '');
      const fill = document.createElement('span');
      fill.className = 'preview-bubble__swatch-fill';
      if (item.style) fill.style.cssText = item.style.replace(/--swatch-background:/g, 'background:');
      btn.appendChild(fill);
      if (item.disabled) {
        const overlay = document.createElement('span');
        overlay.className = 'preview-bubble__swatch-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        btn.appendChild(overlay);
      }
      swatchRoot.appendChild(btn);
    });
  };

  const openPreviewBubble = (state, lineId, anchorItem) => {
    const data = state.get().get(lineId);
    if (!data) return;
    const wrapper = document.querySelector(`.product-card-clickable[data-product-id="${data.productId}"]`);
    const { optionPos, items } = wrapper ? getPreviewSwatchCandidates(wrapper, state, lineId) : { optionPos: null, items: [] };
    removePreviewBubble();
    const bubble = document.createElement('div');
    bubble.className = 'preview-bubble';
    bubble.setAttribute('data-preview-bubble', '');
    bubble.dataset.lineId = lineId;
    bubble.dataset.productId = data.productId;
    bubble.dataset.variantId = data.variantId;
    bubble.dataset.optionPos = optionPos || '';
    const swatchRoot = document.createElement('div');
    swatchRoot.className = 'preview-bubble__swatches';
    const divider = document.createElement('span');
    divider.className = 'preview-bubble__divider';
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'preview-bubble__delete';
    removeBtn.setAttribute('data-preview-bubble-delete', lineId);
    removeBtn.setAttribute('aria-label', '??');
    removeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.75 4.168V18.335H16.25V4.168H3.75Z" stroke="#141529" stroke-width="1.4" stroke-linejoin="round"/><path d="M8.334 8.332V13.749" stroke="#141529" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.666 8.332V13.749" stroke="#141529" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.666 4.168H18.333" stroke="#141529" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.666 4.168L8.036 1.668H11.99L13.333 4.168H6.666Z" stroke="#141529" stroke-width="1.4" stroke-linejoin="round"/></svg>';
    const tail = document.createElement('span');
    tail.className = 'preview-bubble__tail';
    bubble.appendChild(swatchRoot);
    if (items.length) bubble.appendChild(divider);
    bubble.appendChild(removeBtn);
    bubble.appendChild(tail);
    fillPreviewBubbleSwatches(bubble, wrapper, data, items, optionPos);
    const parent = anchorItem || document.querySelector(`.preview-item[data-line-id="${lineId}"]`);
    const grid = parent?.closest('[data-preview-grid]');
    if (!parent || !grid) return;
    grid.appendChild(bubble);
    bubble._anchor = parent;
    positionPreviewBubble(bubble, parent);
    const reposition = () => positionPreviewBubble(bubble, parent);
    bubble._repositionPreviewBubble = reposition;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
  };

  const handlePreviewBubble = (state) => async (e) => {
    const deleteBtn = e.target.closest('[data-preview-bubble-delete]');
    if (deleteBtn) {
      e.stopPropagation();
      const lineId = deleteBtn.dataset.previewBubbleDelete;
      const row = lineId ? state.get().get(lineId) : null;
      const modelKey = row?.modelKey || lineId;
      get3DViewer()?.removeRegisteredModelByKey?.(modelKey);
      state.remove(lineId);
      removePreviewBubble();
      return;
    }

    const colorBtn = e.target.closest('[data-preview-bubble-color]');
    if (colorBtn) {
      e.stopPropagation();
      if (colorBtn.disabled || colorBtn.classList.contains('preview-bubble__swatch--disabled')) return;
      const bubble = colorBtn.closest('[data-preview-bubble]');
      if (!bubble) return;
      const lineId = bubble.dataset.lineId;
      const productId = bubble.dataset.productId;
      const variantId = bubble.dataset.variantId;
      const optionPos = bubble.dataset.optionPos;
      const colorValue = colorBtn.dataset.previewBubbleColor;
      if (!lineId || !productId || !variantId || !optionPos || !colorValue) return;
      const wrapper = document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);
      if (!wrapper) return;
      const picked = pickVariantForColor(wrapper, variantId, optionPos, colorValue, state, lineId);
      if (!picked) {
        showNotification(c3dT('not_enough_color_inventory'), 'warning');
        return;
      }
      const applied = await applyVariantChangeToExistingModel(state, lineId, wrapper, picked);
      if (!applied) {
        showNotification(c3dT('color_apply_failed'), 'warning');
        return;
      }
      bubble.dataset.variantId = String(picked.id);
      const fresh = state.get().get(lineId);
      const sw = getPreviewSwatchCandidates(wrapper, state, lineId);
      fillPreviewBubbleSwatches(bubble, wrapper, fresh, sw.items, sw.optionPos);
      positionPreviewBubble(bubble, bubble._anchor);
      return;
    }

    const previewItem = e.target.closest('.preview-item');
    if (previewItem?.dataset.lineId) {
      e.stopPropagation();
      const existingBubble = document.querySelector(`[data-preview-bubble][data-line-id="${previewItem.dataset.lineId}"]`);
      if (existingBubble) {
        removePreviewBubble();
      } else {
        openPreviewBubble(state, previewItem.dataset.lineId, previewItem);
      }
      return;
    }

    const insidePlacementUi =
      e.target.closest('.preview-item')
      || e.target.closest('[data-preview-bubble]')
      || e.target.closest('[data-preview-grid]')
      || e.target.closest('.product-card-clickable')
      || e.target.closest('[data-3d-container]');
    if (!insidePlacementUi) {
      removePreviewBubble();
      if (pending3dPlacement) {
        void cancelPending3dPlacement();
      } else {
        getVendor()?.hidePlacementZones?.();
      }
    }
  };

  const handleClearAll = (state) => async () => {
    const mySeq = ++loadOpSeq;

    hideModelActionBubble();
    removePreviewBubble();
    clearPending3dPlacement();
    getVendor()?.hidePlacementZones?.();

    const singleEntry = Array.from(state.get().entries()).find(([, data]) => data?.group === 'single') || null;

    if (!singleEntry) {
      state.clear();
      clearCustomizationSessionDraft();
      await clear3DModel();
      hideModelActionBubble();
      return;
    }

    const keepId = singleEntry[0];
    const hadOtherLines = Array.from(state.get().keys()).some((id) => id !== keepId);
    const keepData = { ...singleEntry[1] };

    await clearAccessorySelections(state, { remove3dModels: hadOtherLines });
    if (loadOpSeq !== mySeq) return;

    state.batch(() => {
      Array.from(state.get().keys()).forEach((id) => {
        if (id !== keepId) state.remove(id);
      });
      state.add(keepId, keepData);
    });

    if (!hadOtherLines) {
      hideModelActionBubble();
      return;
    }

    persistCustomizationSessionDraft(state);
  };

  const openCartModal = (state) => {
    const modal = document.querySelector('[data-cart-modal]');
    const modalBody = document.querySelector('[data-cart-modal-body]');
    if (!modal || !modalBody) return;

    const selected = state.get();
    if (selected.size === 0) { showNotification(c3dT('select_at_least_one'), 'warning'); return; }

    modalBody.innerHTML = '';

    selected.forEach((data, lineId) => {
      const wrapper = document.querySelector(`.product-card-clickable[data-product-id="${data.productId}"]`);
      const productImage = data.image
        || getVariantById(wrapper, data.variantId)?.image
        || wrapper?.dataset.productImage
        || '';

      const item = document.createElement('div');
      item.className = 'customization-cart-drawer__item';
      item.dataset.lineId = lineId;
      item.innerHTML = `
        <div class="customization-cart-drawer__checkbox-wrapper">
          <input type="checkbox" class="customization-cart-drawer__checkbox" checked data-cart-item-checkbox="${lineId}">
        </div>
        ${productImage ? `
        <div class="customization-cart-drawer__image-wrapper">
          <img src="${productImage}" alt="${data.title}" class="customization-cart-drawer__image" loading="lazy">
        </div>` : ''}
        <div class="customization-cart-drawer__info">
          <div class="customization-cart-drawer__product-title-row">
            <h4 class="customization-cart-drawer__product-title">${data.title}</h4>
            <button class="customization-cart-drawer__delete-btn" data-cart-item-delete="${lineId}" aria-label="${c3dT('delete_item')}">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6" clip-path="url(#clip0_2155_5728)">
                  <path d="M3.75 4.16797V18.3346H16.25V4.16797H3.75Z" stroke="#141529" stroke-width="1.36364" stroke-linejoin="round"/>
                  <path d="M8.33398 8.33203V13.7487" stroke="#141529" stroke-width="1.36364" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.666 8.33203V13.7487" stroke="#141529" stroke-width="1.36364" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M1.66602 4.16797H18.3327" stroke="#141529" stroke-width="1.36364" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6.66602 4.16797L8.03643 1.66797H11.9898L13.3327 4.16797H6.66602Z" stroke="#141529" stroke-width="1.36364" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_2155_5728">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
          ${data.variantTitle ? `<div class="customization-cart-drawer__variant-title">${data.variantTitle}</div>` : ''}
          <div class="customization-cart-drawer__price-row">
            <div class="customization-cart-drawer__quantity-selector">
              <button class="customization-cart-drawer__quantity-decrease" data-cart-item-decrease="${lineId}">
                <img src="${window.QUANTITY_DECREASE_ICON_URL || '/assets/icon-quantity-decrease.png'}" alt="${c3dT('quantity_decrease_alt')}" width="16" height="16">
              </button>
              <input type="number" class="customization-cart-drawer__quantity-input" value="${data.quantity || 1}" min="1" data-cart-item-quantity="${lineId}" readonly>
              <button class="customization-cart-drawer__quantity-increase" data-cart-item-increase="${lineId}">
                <img src="${window.QUANTITY_INCREASE_ICON_URL || '/assets/icon-quantity-increase.png'}" alt="${c3dT('quantity_increase_alt')}" width="16" height="16">
              </button>
            </div>
            <div class="customization-cart-drawer__product-price">${formatMoney(data.price)}</div>
          </div>
        </div>
      `;
      modalBody.appendChild(item);
    });

    updateCartModalTotal(state);
    updateSelectAllState();
    requestAnimationFrame(() => {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
  };

  const updateCartModalTotal = (state) => {
    const footer = document.querySelector('[data-cart-modal-footer]');
    if (!footer) return;
    const selected = state.get();
    let total = 0;
    document.querySelectorAll('[data-cart-item-checkbox]:checked').forEach(cb => {
      const lineId = cb.dataset.cartItemCheckbox;
      const data = selected.get(lineId);
      if (data) {
        const qty = parseInt(document.querySelector(`[data-cart-item-quantity="${lineId}"]`)?.value) || 1;
        total += data.price * qty;
      }
    });
    const el = footer.querySelector('[data-cart-modal-total-price]');
    if (el) el.textContent = formatMoney(total);
  };

  const updateSelectAllState = () => {
    const selectAll = document.querySelector('[data-cart-select-all]');
    if (!selectAll) return;
    const all     = document.querySelectorAll('[data-cart-item-checkbox]');
    const checked = document.querySelectorAll('[data-cart-item-checkbox]:checked');
    selectAll.checked = all.length > 0 && all.length === checked.length;
  };

  const closeCartModal = () => {
    const modal = document.querySelector('[data-cart-modal]');
    if (!modal) return;
    modal.classList.remove('active');
    setTimeout(() => { document.body.style.overflow = ''; }, 300);
  };

  const validateCheckoutLinesStock = (state, lineIds, getLineQty) => {
    const byVariant = new Map();
    for (const lineId of lineIds) {
      const data = state.get().get(lineId);
      if (!data) continue;
      const raw = getLineQty(lineId, data);
      const q = Math.max(1, parseInt(String(raw), 10) || 1);
      const vid = String(data.variantId);
      byVariant.set(vid, (byVariant.get(vid) || 0) + q);
    }
    for (const [variantId, needQty] of byVariant) {
      let variant = null;
      for (const lineId of lineIds) {
        const data = state.get().get(lineId);
        if (!data || String(data.variantId) !== variantId) continue;
        const w = document.querySelector(`.product-card-clickable[data-product-id="${data.productId}"]`);
        if (w) {
          const v = getVariantById(w, variantId);
          if (v) { variant = v; break; }
        }
      }
      if (!variant || variant.available === false) {
        showNotification(c3dT('select_pergola_first'), 'warning');
        return false;
      }
      const cap = getVariantInventoryCap(variant);
      if (cap !== Infinity && needQty > cap) {
        showNotification(c3dT('variant_unavailable'), 'warning');
        return false;
      }
    }
    return true;
  };

  const addToCart = async (state, lineIds) => {
    const selected = state.get();
    const items = lineIds.reduce((acc, lineId) => {
      const data = selected.get(lineId);
      if (data) {
        const qty = parseInt(document.querySelector(`[data-cart-item-quantity="${lineId}"]`)?.value) || data.quantity || 1;
        acc.push({ id: data.variantId, quantity: qty });
      }
      return acc;
    }, []);

    if (!items.length) { showNotification(c3dT('select_at_least_one'), 'warning'); return false; }

    if (!validateCheckoutLinesStock(state, lineIds, (lid, data) =>
      document.querySelector(`[data-cart-item-quantity="${lid}"]`)?.value ?? data.quantity ?? 1)) {
      return false;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json();
        const apiMsg = error.description || error.message || '';
        const isMaxQtyOrStock = response.status === 422 && (apiMsg.includes('maximum quantity') || apiMsg.includes('already in your cart') || apiMsg.includes('stock'));
        const msg = isMaxQtyOrStock && window.__addonCartMessages?.max_quantity_in_cart
          ? window.__addonCartMessages.max_quantity_in_cart
          : (apiMsg || window.__addonCartMessages?.add_to_cart_error || 'There was an error adding to cart. Please try again.');
        throw new Error(msg);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));

      const cart = await fetch('/cart.js').then(r => r.json());
      const countEl = document.querySelector('cart-count-bubble')
        ?.querySelector('.cart-count-bubble, .count-bubble, [data-count]');
      if (countEl) countEl.textContent = cart.item_count;

      removeLinesFromCustomizationState(state, lineIds);
      return true;
    } catch (error) {
      console.error('[Customization] Add to cart failed:', error);
      showNotification(error.message || c3dT('add_to_cart_error'), 'error');
      return false;
    }
  };

  const openShopifyCartDrawer = () => {
    document.dispatchEvent(new CustomEvent('cart:open'));

    const selectors = [
      '[aria-controls*="cart"]', '[href*="/cart"]', '[data-action*="cart"]',
      '[data-drawer*="cart"]', '.cart-drawer-toggle', '[data-cart-toggle]',
      '.header__cart', 'cart-count-bubble',
    ];

    let clicked = false;
    document.querySelectorAll(selectors.join(',')).forEach(btn => {
      if (!clicked && btn.offsetParent !== null) { btn.click(); clicked = true; }
    });

    if (!clicked) {
      document.querySelectorAll('cart-drawer, [data-drawer="cart"], [data-cart-drawer]').forEach(drawer => {
        if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          drawer.setAttribute('open', '');
        }
      });
    }
  };

  const handleModalViewCart = (state) => async () => {
    const modal = document.querySelector('[data-cart-modal]');
    if (!modal) return;

    const lineIds = Array.from(modal.querySelectorAll('[data-cart-item-checkbox]:checked'))
      .map(cb => cb.dataset.cartItemCheckbox);
    if (!lineIds.length) { showNotification(c3dT('select_at_least_one'), 'warning'); return; }

    const btn = document.querySelector('[data-cart-modal-add]');
    const originalText = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = c3dT('adding'); }

    const success = await addToCart(state, lineIds);
    if (btn) { btn.disabled = false; btn.textContent = originalText; }

    if (success) {
      closeCartModal();
      setTimeout(openShopifyCartDrawer, 300);
    }
  };

  const handleModalCheckout = (state) => async () => {
    const modal = document.querySelector('[data-cart-modal]');
    if (!modal) return;

    const lineIds = Array.from(modal.querySelectorAll('[data-cart-item-checkbox]:checked'))
      .map(cb => cb.dataset.cartItemCheckbox);
    if (!lineIds.length) { showNotification(c3dT('select_at_least_one'), 'warning'); return; }

    const btn = document.querySelector('[data-cart-modal-checkout]');
    if (btn) { btn.disabled = true; btn.textContent = c3dT('loading'); }

    const success = await addToCart(state, lineIds);

    if (success) {
      closeCartModal();
      window.location.href = '/checkout';
    } else if (btn) {
      btn.disabled = false;
      btn.textContent = c3dT('checkout');
    }
  };

  const addToCartDirect = async (state, lineIds) => {
    const selected = state.get();
    const items = lineIds.reduce((acc, lineId) => {
      const data = selected.get(lineId);
      if (data) acc.push({ id: data.variantId, quantity: data.quantity || 1 });
      return acc;
    }, []);

    if (!items.length) { showNotification(c3dT('select_at_least_one'), 'warning'); return false; }

    if (!validateCheckoutLinesStock(state, lineIds, (_lid, data) => data.quantity || 1)) return false;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json();
        const apiMsg = error.description || error.message || '';
        const isMaxQtyOrStock = response.status === 422 && (apiMsg.includes('maximum quantity') || apiMsg.includes('already in your cart') || apiMsg.includes('stock'));
        const msg = isMaxQtyOrStock && window.__addonCartMessages?.max_quantity_in_cart
          ? window.__addonCartMessages.max_quantity_in_cart
          : (apiMsg || window.__addonCartMessages?.add_to_cart_error || 'There was an error adding to cart. Please try again.');
        throw new Error(msg);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));

      removeLinesFromCustomizationState(state, lineIds);
      return true;
    } catch (error) {
      console.error('[Customization] Add to cart failed:', error);
      showNotification(error.message || c3dT('add_to_cart_error'), 'error');
      return false;
    }
  };

  const openTipsConfirmModal = (state) => {
    if (!document.querySelector('#customization-tips-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'customization-tips-modal-styles';
      style.textContent = `
        .customization-tips-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 10001; display: flex; align-items: center; justify-content: center;
        }
        .customization-tips-modal {
          background: #fff; overflow: hidden;
          width: 420px; max-width: calc(100vw - 40px);
          box-shadow: 0 8px 40px rgba(0,0,0,0.18);
          font-family: system-ui, -apple-system, sans-serif;
        }
        .customization-tips-modal__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20.5px 16.46px 20.5px 25.48px;
        }
        .customization-tips-modal__title {
          font-size: 16px; font-weight: 500; color: #141529; margin: 0; line-height: 1;
        }
        .customization-tips-modal__close {
          background: none; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .customization-tips-modal__divider {
          border: none; border-top: 1px solid #D3D2D080; margin: 0;
        }
        .customization-tips-modal__content {
          padding: 42px 28px 23px 28px;
        }
        .customization-tips-modal__body {
          font-size: 14px; color: #141529; text-align: center; margin: 0 0 32px; line-height: 1.5;
        }
        .customization-tips-modal__actions {
          display: flex; gap: 16px;
        }
        .customization-tips-modal__btn {
          flex: 1; border-radius: 101px; border: none;
          font-size: 16px; font-weight: 600; cursor: pointer;
          padding: 12px; line-height: 20px; text-align: center;
        }
        .customization-tips-modal__btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .customization-tips-modal__btn--yes {
          background: #F4CE52; color: #fff;
        }
        .customization-tips-modal__btn--no {
          background: #f8f8f8; color: #141529;
        }
        @media screen and (max-width: 999px) {
          .customization-tips-modal__header {
            padding: 14px 18px;
          }
          .customization-tips-modal__content {
            padding: 26px;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.className = 'customization-tips-overlay';
    overlay.innerHTML = `
      <div class="customization-tips-modal" role="dialog" aria-modal="true" aria-labelledby="tips-modal-title">
        <div class="customization-tips-modal__header">
          <p class="customization-tips-modal__title" id="tips-modal-title">${c3dT('tips_title')}</p>
          <button class="customization-tips-modal__close" aria-label="${c3dT('close')}">
            <img src="${window.TIPS_MODAL_CLOSE_ICON_URL || ''}" alt="" width="25" height="25">
          </button>
        </div>
        <hr class="customization-tips-modal__divider">
        <div class="customization-tips-modal__content">
          <p class="customization-tips-modal__body">${c3dT('tips_body')}</p>
          <div class="customization-tips-modal__actions">
            <button class="customization-tips-modal__btn customization-tips-modal__btn--yes" data-tips-yes>${c3dT('tips_yes')}</button>
            <button class="customization-tips-modal__btn customization-tips-modal__btn--no" data-tips-no>${c3dT('tips_no')}</button>
          </div>
        </div>
      </div>
    `;

    const closeModal = () => overlay.remove();

    overlay.querySelector('.customization-tips-modal__close').addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });

    const handleChoice = async (includeMain) => {
      const yesBtn = overlay.querySelector('[data-tips-yes]');
      const noBtn = overlay.querySelector('[data-tips-no]');
      yesBtn.disabled = true;
      noBtn.disabled = true;

      const selected = state.get();
      const lineIds = [];
      selected.forEach((data, lineId) => {
        if (includeMain || data.group !== 'single') lineIds.push(lineId);
      });

      const success = await addToCartDirect(state, lineIds);
      if (success) {
        closeModal();
        window.location.href = '/cart';
      } else {
        yesBtn.disabled = false;
        noBtn.disabled = false;
      }
    };

    overlay.querySelector('[data-tips-yes]').addEventListener('click', () => handleChoice(true));
    overlay.querySelector('[data-tips-no]').addEventListener('click', () => handleChoice(false));

    document.body.appendChild(overlay);
  };

  const initializeDefaultVariants = (state) => {
    document.querySelectorAll('.product-card-clickable').forEach(wrapper => {
      const script = wrapper.querySelector('[data-product-variants]');
      if (!script) return;
      try {
        const variants = JSON.parse(script.textContent);
        if (!variants?.length) return;

        const defaultVariantId = wrapper.dataset.variantId || variants[0].id;
        const defaultVariant   = variants.find(v => String(v.id) === String(defaultVariantId)) || variants[0];

        wrapper.dataset.variantId    = defaultVariantId;
        wrapper.dataset.variantPrice = defaultVariant.price;

        const select = wrapper.querySelector('variant-selects select, select[name="id"]');
        if (select?.tagName === 'SELECT' && select.value !== String(defaultVariantId)) {
          select.value = defaultVariantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const radios = wrapper.querySelector('variant-radios');
        if (radios) {
          const radio = radios.querySelector(`input[type="radio"][value="${defaultVariantId}"]`);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
        if (swatchList) {
          const optionPos = swatchList.dataset.optionPosition;
          if (optionPos) {
            const defaultColorValue = defaultVariant[`option${optionPos}`];
            const swatchInputs = Array.from(wrapper.querySelectorAll('[data-cpc-swatch-input]'));
            let matched = false;
            swatchInputs.forEach(input => {
              if (input.disabled) return;
              const shouldBeChecked =
                String(input.value || '').trim() === String(defaultColorValue || '').trim();
              if (input.checked !== shouldBeChecked) input.checked = shouldBeChecked;
              if (shouldBeChecked) matched = true;
            });
            if (!matched) {
              const firstEnabled = swatchInputs.find(i => !i.disabled);
              if (firstEnabled) firstEnabled.checked = true;
            }
          }
        }

        applyCpcSwatchInventoryState(wrapper, state);

        const resolvedId = getCurrentVariantId(wrapper, state) || defaultVariantId;
        const resolvedVariant = getVariantById(wrapper, resolvedId);
        if (resolvedVariant) {
          wrapper.dataset.variantId = String(resolvedVariant.id);
          wrapper.dataset.variantPrice = resolvedVariant.price;
          updateCpcMainImage(wrapper, resolvedVariant);
        }

        updateCpcMetaRowVisibility(wrapper, state);
      } catch (e) {
        console.error('[Customization] Failed to initialize default variant:', e);
      }
    });
  };

  const preloadC3dSdkScript = () => {
    if (c3dSdkPreloadStarted) return;
    c3dSdkPreloadStarted = true;
    if (document.querySelector('script[data-c3d-sdk], script[src*="index.global.js"]')) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = C3D_SDK_URL;
    document.head.appendChild(link);
  };

  const getCatalogIndex = () => {
    const raw = window.CUSTOMIZATION_3D_CATALOG_INDEX;
    return Array.isArray(raw) ? raw : [];
  };

  const resolvePanelIdFromCatalog = (productId, handle) => {
    const index = getCatalogIndex();
    const pid = productId != null ? String(productId).trim() : '';
    const h = handle != null ? String(handle).trim() : '';
    if (pid) {
      const hit = index.find((row) => String(row.id) === pid);
      if (hit?.p != null) return String(hit.p);
    }
    if (h) {
      const hit = index.find((row) => row.h === h);
      if (hit?.p != null) return String(hit.p);
    }
    return '1';
  };

  const getSectionRenderUrl = (panelId) => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    const sectionId = wrapper?.dataset?.sectionId;
    if (!sectionId) return null;
    const root = (typeof window.Shopify !== 'undefined' && window.Shopify.routes?.root)
      ? window.Shopify.routes.root
      : '/';
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    const path = window.location.pathname || '/';
    const url = new URL(`${base}${path}`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('render_panel', String(panelId));
    return url.toString();
  };

  const refreshPanelAfterLazyLoad = (panel, state) => {
    if (!panel || !state) return;
    filterProducts(panel, state);
    syncProductCards(state);
    initializeDefaultVariants(state);
  };

  const ensurePanelProductsLoaded = async (panelId) => {
    const id = String(panelId || '').trim();
    if (!id) return true;
    const panel = document.querySelector(`.panel-content[data-panel-id="${id}"]`);
    if (!panel) return false;
    if (panel.querySelector('[data-c3d-panel-products]')) {
      panel.dataset.c3dProductsLoaded = 'true';
      return true;
    }
    if (panel.dataset.c3dProductsLoaded === 'true') return true;

    const placeholder = panel.querySelector('[data-c3d-products-placeholder]');
    if (!placeholder) {
      panel.dataset.c3dProductsLoaded = 'true';
      return true;
    }

    if (panelProductsLoadCache.has(id)) return panelProductsLoadCache.get(id);

    const job = (async () => {
      const url = getSectionRenderUrl(id);
      if (!url) return false;
      placeholder.classList.add('is-loading');
      placeholder.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const chunk =
          doc.querySelector(`[data-c3d-panel-products][data-panel-id="${id}"]`)
          || doc.querySelector('[data-c3d-panel-products]');
        const nodes = chunk
          ? Array.from(chunk.childNodes).map((n) => n.cloneNode(true))
          : Array.from(doc.body.childNodes).map((n) => n.cloneNode(true));
        placeholder.replaceWith(...nodes);
        panel.dataset.c3dProductsLoaded = 'true';
        const state = document.querySelector('[data-3d-container]')?.__c3dState;
        refreshPanelAfterLazyLoad(panel, state);
        return true;
      } catch (err) {
        console.warn('[Customization] lazy panel load failed', { panelId: id, err });
        const hint = placeholder.querySelector('[data-c3d-panel-hint]');
        if (hint) hint.innerHTML = `<p>${c3dT('could_not_load_products')}</p>`;
        panelProductsLoadCache.delete(id);
        return false;
      }
    })();

    panelProductsLoadCache.set(id, job);
    return job;
  };

  const isPdpCustomizationEntry = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('variant')) return true;
      return !!sessionStorage.getItem(PDP_ADDONS_STORAGE_KEY);
    } catch {
      return false;
    }
  };

  const ensureHotspotsLoadedOnce = async () => {
    if (hotspotsLoadedOnce) return;
    hotspotsLoadedOnce = true;
    const ready = await ensure3DReady();
    if (!ready) {
      hotspotsLoadedOnce = false;
      return;
    }
    try {
      await getVendor()?.fetchAndRenderHotspots?.();
    } catch (err) {
      hotspotsLoadedOnce = false;
      console.warn('[Customization] hotspot load failed:', err);
    }
  };

  const warmCustomization3dEntry = () => {
    preloadC3dSdkScript();
    if (isPdpCustomizationEntry()) {
      get3DViewer()?.whenReady?.().catch((err) => {
        console.warn('[Customization] eager 3D warm-up failed:', err);
      });
      return;
    }
    const wrapper = document.querySelector('.customization-3d-wrapper');
    if (!wrapper) return;
    const onIntent = () => {
      preloadC3dSdkScript();
      wrapper.removeEventListener('pointerenter', onIntent);
      wrapper.removeEventListener('focusin', onIntent);
    };
    wrapper.addEventListener('pointerenter', onIntent, { once: true, passive: true });
    wrapper.addEventListener('focusin', onIntent, { once: true });
  };

  const setupLazyPanelPreload = () => {
    document.querySelectorAll('.primary-menu-item[data-menu-id]').forEach((btn) => {
      btn.addEventListener('pointerenter', () => {
        const panelId = btn.dataset.menuId;
        if (!panelId || panelId === '1') return;
        preloadC3dSdkScript();
        ensurePanelProductsLoaded(panelId).catch(() => {});
      }, { passive: true });
    });
  };

  const update3DModelOnSelectionChange = (state) => {
    const selected = state.get();
    if (!selected.size) { clear3DModel(); }
  };

  const findWrapperForDeepLink = (variantId, productId, handle) => {
    let byVariant = null;
    document.querySelectorAll('.product-card-clickable').forEach((card) => {
      const variants = getVariantData(card);
      if (variants?.some((v) => String(v.id) === String(variantId))) byVariant = card;
    });
    if (byVariant) return byVariant;

    if (productId) {
      const byId = document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);
      if (byId) return byId;
    }
    if (handle) {
      return document.querySelector(`.product-card-clickable[data-product-handle="${handle}"]`) || null;
    }
    return null;
  };

  const findWrapperForPdpAddon = (variantId, productId, handle) => {
    if (productId) {
      const byId = document.querySelector(`.product-card-clickable[data-product-id="${productId}"]`);
      if (byId && getVariantById(byId, variantId)) return byId;
    }
    if (handle) {
      const byHandle = document.querySelector(`.product-card-clickable[data-product-handle="${handle}"]`);
      if (byHandle && getVariantById(byHandle, variantId)) return byHandle;
    }
    let byVariant = null;
    document.querySelectorAll('.product-card-clickable').forEach((card) => {
      const variants = getVariantData(card);
      if (variants?.some((v) => String(v.id) === String(variantId))) byVariant = card;
    });
    return byVariant;
  };

  const fetchProductJsonByHandle = async (handle) => {
    if (!handle) return null;
    const root = (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const url = `${base}products/${encodeURIComponent(handle)}.js`;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  };

  const variantPriceToCents = (priceField) => {
    if (priceField == null) return 0;
    if (typeof priceField === 'number' && Number.isInteger(priceField) && priceField > 1000) return priceField;
    const n = parseFloat(String(priceField).replace(/[^0-9.]/g, ''));
    return Number.isNaN(n) ? 0 : Math.round(n * 100);
  };

  const imageUrlFromProductJson = (variant, product) => {
    const pick = (img) => {
      if (!img) return '';
      if (typeof img === 'string') return img;
      if (img.src) return img.src;
      return '';
    };
    return pick(variant.featured_image) || pick(product.featured_image)
      || (product.images && product.images[0] ? pick(product.images[0]) : '');
  };

  const addProductSelectionFromFetchedProduct = (state, product, variantId, quantity) => {
    const variant = (product.variants || []).find((v) => String(v.id) === String(variantId));
    if (!variant) return false;

    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const image = imageUrlFromProductJson(variant, product);
    const priceCents = variantPriceToCents(variant.price);

    const productId = String(product.id);
    const lineId = createLineId(productId, variant.id);
    const modelKey = getModelKeyForLine(lineId, productId, 'multi');
    state.add(lineId, {
      lineId,
      modelKey,
      productId,
      variantId:    variant.id,
      price:        priceCents,
      title:        product.title,
      handle:       product.handle,
      variantTitle: variant.title || '',
      options:      getOptionsFromVariant(variant),
      quantity:     qty,
      image,
      group:        'multi',
    });
    return true;
  };

  const setVariantDomOnWrapper = (wrapper, variantId) => {
    const variant = getVariantById(wrapper, variantId);
    if (!variant) return false;

    wrapper.dataset.variantId = variantId;
    wrapper.dataset.variantPrice = variant.price;

    const select = wrapper.querySelector('variant-selects select, select[name="id"]');
    if (select?.tagName === 'SELECT') {
      select.value = String(variantId);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const radios = wrapper.querySelector('variant-radios');
    if (radios) {
      const radio = radios.querySelector(`input[type="radio"][value="${variantId}"]`);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const swatchList = wrapper.querySelector('[data-cpc-swatch-list]');
    if (swatchList) {
      const optionPos = swatchList.dataset.optionPosition;
      if (optionPos) {
        const selectedValue = variant[`option${optionPos}`];
        const swatchInputs = Array.from(wrapper.querySelectorAll('[data-cpc-swatch-input]'));
        let matched = false;
        swatchInputs.forEach((input) => {
          const shouldCheck = !input.disabled
            && String(input.value || '').trim() === String(selectedValue || '').trim();
          if (input.checked !== shouldCheck) input.checked = shouldCheck;
          if (shouldCheck) matched = true;
        });
        if (!matched) {
          const firstEnabled = swatchInputs.find((input) => !input.disabled);
          if (firstEnabled) firstEnabled.checked = true;
        }
      }
    }

    updateCpcMainImage(wrapper, variant);
    return true;
  };

  const addProductSelectionFromWrapper = (state, wrapper, variantId, quantity = 1, modelKeyOverride = '') => {
    const { productId, productTitle, productHandle } = wrapper.dataset;
    const selectionGroup = wrapper.dataset.selectionGroup || 'multi';

    const variant = getVariantById(wrapper, variantId);
    if (!variant) return null;

    const qty = Math.max(1, parseInt(quantity, 10) || 1);

    const lineId = createLineId(productId, variant.id);
    const modelKey = String(modelKeyOverride || '').trim()
      || resolveModelKeyForCard(lineId, productId, selectionGroup, wrapper);
    const newData = {
      lineId,
      modelKey,
      productId,
      variantId:    variant.id,
      price:        variant.price || parseFloat(wrapper.dataset.variantPrice) || 0,
      title:        productTitle,
      handle:       productHandle,
      variantTitle: variant.title || '',
      options:      getOptionsFromVariant(variant),
      quantity:     qty,
      image:        variant.image || wrapper.dataset.productImage,
      group:        selectionGroup,
      bubbleMode:   getProductBubbleModeById(productId),
      c3dCompatibleSize: getC3dCompatibleSizeFromWrapper(wrapper),
    };

    if (selectionGroup === 'single') {
      state.batch(() => {
        const toRemove = [];
        state.get().forEach((data, id) => {
          if (data.group === 'single') toRemove.push(id);
        });
        toRemove.forEach((id) => state.remove(id));
        state.add(lineId, newData);
      });
      const pergolaSku = String(variant.sku || '').trim();
      if (pergolaSku) getVendor()?.setPergolaSku?.(pergolaSku);
    } else {
      state.add(lineId, newData);
    }
    return lineId;
  };

  const applyProductSelectionFromPdp = async (state, wrapper, variantId) => {
    const variant = getVariantById(wrapper, variantId);
    if (!variant) return;
    const placementResult = await queuePreparePlacementBySku(wrapper, variant);
    if (!placementResult) return;
    if (placementResult.mode === 'placement_zone') return;
    const placedModelId = Array.isArray(placementResult.modelIds)
      ? String(placementResult.modelIds[0] || '').trim()
      : '';
    addProductSelectionFromWrapper(state, wrapper, variantId, 1, placedModelId);
    syncProductCards(state);
    syncPrimary3dHostDataset(state);
  };

  const applyPdpAddonsFromStorage = async (state) => {
    let raw = null;
    try {
      raw = sessionStorage.getItem(PDP_ADDONS_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      sessionStorage.removeItem(PDP_ADDONS_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    const acc = Array.isArray(parsed.accessories) ? parsed.accessories : [];
    const sw = Array.isArray(parsed.sidewalls) ? parsed.sidewalls : [];
    const rows = [...acc, ...sw];
    if (!rows.length) return;

    for (const row of rows) {
      const vid = row.variantId != null ? String(row.variantId) : '';
      const qty = row.quantity;
      const pid = row.productId != null ? String(row.productId).trim() : '';
      const phandle = row.productHandle != null ? String(row.productHandle).trim() : '';
      if (!vid) continue;

      const addonPanelId = resolvePanelIdFromCatalog(pid, phandle);
      await ensurePanelProductsLoaded(addonPanelId);

      const wrapper = findWrapperForPdpAddon(vid, pid || null, phandle || null);

      if (wrapper) {
        const panel = wrapper.closest('.panel-content');
        if (panel) {
          panel.querySelectorAll('.secondary-menu-btn.active').forEach((b) => b.classList.remove('active'));
          filterProducts(panel, state);
          syncProductCards(state);
        }

        wrapper.style.display = '';
        setVariantDomOnWrapper(wrapper, vid);
        await new Promise((r) => setTimeout(r, 40));

        addProductSelectionFromWrapper(state, wrapper, vid, qty);
        continue;
      }

      if (phandle) {
        const product = await fetchProductJsonByHandle(phandle);
        if (product && addProductSelectionFromFetchedProduct(state, product, vid, qty)) continue;
      }

      console.warn('[Customization] PDP addon: not in 3D grid and cannot load product JSON', { vid, pid, phandle });
    }
  };

  const applyPdpDeepLink = async (state) => {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (!variantId) return;

    const productId = params.get('product_id');
    const handle = params.get('handle');

    const catalogPanelId = resolvePanelIdFromCatalog(productId, handle);
    await ensurePanelProductsLoaded(catalogPanelId);

    const wrapper = findWrapperForDeepLink(variantId, productId, handle);
    if (!wrapper) {
      console.warn('[Customization] PDP deep link: product not found in 3D catalog.');
      return;
    }

    const panel = wrapper.closest('.panel-content');
    if (!panel) return;

    const menuPanelId = panel.dataset.panelId;
    const menuBtn = document.querySelector(`.primary-menu-item[data-menu-id="${menuPanelId}"]`);
    if (menuBtn && !menuBtn.classList.contains('active')) {
      menuBtn.click();
    }

    await new Promise((r) => setTimeout(r, 50));

    panel.querySelectorAll('.secondary-menu-btn.active').forEach((b) => b.classList.remove('active'));
    filterProducts(panel, state);
    syncProductCards(state);

    wrapper.style.display = '';
    setVariantDomOnWrapper(wrapper, variantId);
    await new Promise((r) => setTimeout(r, 50));

    await applyProductSelectionFromPdp(state, wrapper, variantId);

    wrapper.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const firstSelectionEntrySorted = (state) => {
    const sorted = Array.from(state.get().entries()).sort(
      (a, b) => (a[1].slotIndex ?? Infinity) - (b[1].slotIndex ?? Infinity)
    );
    return sorted[0] || null;
  };

  const syncPrimary3dHostDataset = (state) => {
    const host = document.querySelector('[data-3d-container]');
    if (!host) return;
    const first = firstSelectionEntrySorted(state);
    if (first) {
      host.dataset.c3dPrimaryLineId = String(first[0]);
      host.dataset.c3dPrimaryProductId = String(first[1].productId);
    } else {
      delete host.dataset.c3dPrimaryLineId;
      delete host.dataset.c3dPrimaryProductId;
    }
  };

  const resync3DFromSelection = async (state) => {
    if (!state) return;
    const sel = state.get();
    if (!sel.size) {
      clearPending3dPlacement();
      getVendor()?.hidePlacementZones?.();
      show3DEmpty();
      return;
    }
    const first = firstSelectionEntrySorted(state);
    if (!first) return;
    const data = first[1];
    const w = document.querySelector(`.product-card-clickable[data-product-id="${data.productId}"]`);
    const variant = w ? getVariantById(w, data.variantId) : null;
    if (variant) await queuePreparePlacementBySku(w, variant);
  };

  const C3D_MOBILE_MQ = window.matchMedia('(max-width: 999px)');
  const C3D_DESKTOP_MQ = window.matchMedia('(min-width: 1000px)');

  let c3dDesktopChromeOffsetPx = null;

  const syncC3dViewportLayout = () => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    const root = document.documentElement;
    if (!wrapper) return;

    if (!C3D_DESKTOP_MQ.matches) {
      c3dDesktopChromeOffsetPx = null;
      root.style.removeProperty('--c3d-chrome-offset');
      return;
    }

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    if (scrollY < 24) {
      c3dDesktopChromeOffsetPx = Math.max(0, Math.ceil(wrapper.getBoundingClientRect().top));
    } else if (c3dDesktopChromeOffsetPx == null) {
      c3dDesktopChromeOffsetPx = Math.max(0, Math.ceil(wrapper.getBoundingClientRect().top + scrollY));
    }

    if (c3dDesktopChromeOffsetPx != null) {
      root.style.setProperty('--c3d-chrome-offset', `${c3dDesktopChromeOffsetPx}px`);
    }

    const bottomBar = wrapper.querySelector('.bottom-bar');
    if (bottomBar) {
      root.style.setProperty('--c3d-bottom-bar-h', `${Math.ceil(bottomBar.offsetHeight)}px`);
    }
  };

  const C3D_STICKY_HEAD_CLASS = 'c3d-mobile-sticky-head';
  let c3dStickyHeadRestore = null;

  const restoreDesktopStickyHeadLayout = () => {
    const stage = document.querySelector('.customization-3d-stage');
    const head = stage?.querySelector(`.${C3D_STICKY_HEAD_CLASS}`);
    const menu = document.querySelector('.customization-3d-wrapper .primary-menu');
    const preview = document.querySelector('.customization-3d-wrapper .preview-area');
    if (!c3dStickyHeadRestore || !stage) {
      head?.remove();
      return;
    }

    const { menuParent, menuNext, previewParent, previewNext } = c3dStickyHeadRestore;
    if (menu && menuParent && menu.parentElement !== menuParent) {
      if (menuNext && menuNext.parentElement === menuParent) {
        menuParent.insertBefore(menu, menuNext);
      } else {
        menuParent.appendChild(menu);
      }
    }
    if (preview && previewParent && preview.parentElement !== previewParent) {
      if (previewNext && previewNext.parentElement === previewParent) {
        previewParent.insertBefore(preview, previewNext);
      } else {
        previewParent.appendChild(preview);
      }
    }
    head?.remove();
  };

  const applyMobileStickyHeadLayout = () => {
    const stage = document.querySelector('.customization-3d-stage');
    const wrapper = document.querySelector('.customization-3d-wrapper');
    const menu = wrapper?.querySelector('.primary-menu');
    const preview = wrapper?.querySelector('.preview-area');
    if (!stage || !menu || !preview) return;

    if (!C3D_MOBILE_MQ.matches) {
      restoreDesktopStickyHeadLayout();
      return;
    }

    if (!c3dStickyHeadRestore) {
      c3dStickyHeadRestore = {
        menuParent: menu.parentElement,
        menuNext: menu.nextElementSibling,
        previewParent: preview.parentElement,
        previewNext: preview.nextElementSibling,
      };
    }

    let head = stage.querySelector(`.${C3D_STICKY_HEAD_CLASS}`);
    if (!head) {
      head = document.createElement('div');
      head.className = C3D_STICKY_HEAD_CLASS;
      stage.insertBefore(head, stage.firstChild);
    }

    if (menu.parentElement !== head) head.appendChild(menu);
    if (preview.parentElement !== head) head.appendChild(preview);
  };

  const initMobileStickyHeadLayout = () => {
    const schedule = () => requestAnimationFrame(applyMobileStickyHeadLayout);
    schedule();
    if (typeof C3D_MOBILE_MQ.addEventListener === 'function') {
      C3D_MOBILE_MQ.addEventListener('change', schedule);
    } else if (typeof C3D_MOBILE_MQ.addListener === 'function') {
      C3D_MOBILE_MQ.addListener(schedule);
    }
    window.addEventListener('resize', schedule, { passive: true });
  };

  let c3dViewportLayoutObserver = null;

  const initC3dViewportLayoutSync = () => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    if (!wrapper) return;

    const schedule = () => requestAnimationFrame(syncC3dViewportLayout);
    const scheduleResize = () => {
      c3dDesktopChromeOffsetPx = null;
      schedule();
    };
    schedule();
    window.addEventListener('resize', scheduleResize, { passive: true });

    const onMqChange = () => {
      c3dDesktopChromeOffsetPx = null;
      schedule();
    };
    if (typeof C3D_DESKTOP_MQ.addEventListener === 'function') {
      C3D_DESKTOP_MQ.addEventListener('change', onMqChange);
    } else if (typeof C3D_DESKTOP_MQ.addListener === 'function') {
      C3D_DESKTOP_MQ.addListener(onMqChange);
    }
    if (typeof C3D_MOBILE_MQ.addEventListener === 'function') {
      C3D_MOBILE_MQ.addEventListener('change', onMqChange);
    } else if (typeof C3D_MOBILE_MQ.addListener === 'function') {
      C3D_MOBILE_MQ.addListener(onMqChange);
    }

    if (typeof ResizeObserver === 'function') {
      c3dViewportLayoutObserver?.disconnect();
      c3dViewportLayoutObserver = new ResizeObserver((entries) => {
        const chromeEntry = entries.some((e) => e.target !== wrapper);
        if (chromeEntry) scheduleResize();
        else schedule();
      });
      c3dViewportLayoutObserver.observe(wrapper);
      const headerGroup = document.getElementById('shopify-section-group-header-group')
        || document.querySelector('.shopify-section-group-header-group, #header-group, .header');
      if (headerGroup) c3dViewportLayoutObserver.observe(headerGroup);
      const pageNav = document.querySelector('.pagenav_con');
      if (pageNav) c3dViewportLayoutObserver.observe(pageNav);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(schedule).catch(() => {});
    }
  };

  const collapseMobileFilterAccordions = () => {
    document.querySelectorAll('[data-filter-group].is-expanded').forEach((group) => {
      group.classList.remove('is-expanded');
    });
    updateMobileFilterBackdrop();
  };

  const updateMobileFilterBackdrop = () => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    const backdrop = wrapper?.querySelector('[data-c3d-filter-backdrop]');
    if (!wrapper || !backdrop) return;

    if (!C3D_MOBILE_MQ.matches) {
      wrapper.classList.remove('c3d-filter-expanded');
      backdrop.hidden = true;
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.style.removeProperty('top');
      backdrop.style.removeProperty('bottom');
      return;
    }

    const activePanel = wrapper.querySelector('.panel-content.active');
    const filterGroups = activePanel?.querySelector('[data-filter-groups]');
    const hasExpanded = !!filterGroups?.querySelector('[data-filter-group].is-expanded');

    if (!hasExpanded || !filterGroups) {
      wrapper.classList.remove('c3d-filter-expanded');
      backdrop.hidden = true;
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.style.removeProperty('top');
      return;
    }

    const bottomBar = wrapper.querySelector('.bottom-bar');
    let footerInset = '0px';
    if (bottomBar) {
      const barRect = bottomBar.getBoundingClientRect();
      footerInset = `${Math.max(0, window.innerHeight - barRect.top)}px`;
    }
    const rect = filterGroups.getBoundingClientRect();
    backdrop.style.top = `${Math.max(0, rect.bottom)}px`;
    backdrop.style.bottom = footerInset;
    backdrop.hidden = false;
    backdrop.setAttribute('aria-hidden', 'false');
    wrapper.classList.add('c3d-filter-expanded');
  };

  const initMobileBottomBarDock = () => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    const anchor = wrapper?.querySelector('[data-c3d-bottom-bar-anchor]');
    if (!wrapper || !anchor) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      if (!C3D_MOBILE_MQ.matches) {
        wrapper.classList.remove('c3d-bottom-bar-stuck');
        return;
      }
      const dockTop = anchor.getBoundingClientRect().top;
      wrapper.classList.toggle('c3d-bottom-bar-stuck', dockTop > window.innerHeight);
    };

    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (typeof C3D_MOBILE_MQ.addEventListener === 'function') {
      C3D_MOBILE_MQ.addEventListener('change', schedule);
    } else if (typeof C3D_MOBILE_MQ.addListener === 'function') {
      C3D_MOBILE_MQ.addListener(schedule);
    }
    if (typeof ResizeObserver !== 'undefined') {
      try {
        const ro = new ResizeObserver(schedule);
        ro.observe(anchor);
      } catch (_) {
      }
    }
    schedule();
  };

  const initMobileScrollToTop = () => {
    const btn = document.querySelector('[data-c3d-scroll-top]');
    if (!btn) return;

    const SCROLL_SHOW_THRESHOLD = 240;

    const updateScrollTopButton = () => {
      if (!C3D_MOBILE_MQ.matches) {
        btn.hidden = true;
        return;
      }

      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      btn.hidden = scrollY < SCROLL_SHOW_THRESHOLD;

      const wrapper = document.querySelector('.customization-3d-wrapper');
      const bottomBar = wrapper?.querySelector('.bottom-bar');
      if (bottomBar) {
        const barRect = bottomBar.getBoundingClientRect();
        const insetFromBottom = Math.max(16, window.innerHeight - barRect.top + 12);
        btn.style.bottom = `${insetFromBottom}px`;
      } else {
        btn.style.bottom = '88px';
      }
    };

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    window.addEventListener('scroll', updateScrollTopButton, { passive: true });
    window.addEventListener('resize', updateScrollTopButton);
    if (typeof C3D_MOBILE_MQ.addEventListener === 'function') {
      C3D_MOBILE_MQ.addEventListener('change', updateScrollTopButton);
    } else if (typeof C3D_MOBILE_MQ.addListener === 'function') {
      C3D_MOBILE_MQ.addListener(updateScrollTopButton);
    }
    updateScrollTopButton();
  };

  const initMobileFilterAccordions = () => {
    document.querySelectorAll('[data-filter-groups]').forEach((filterGroups) => {
      const groups = Array.from(filterGroups.querySelectorAll('[data-filter-group]'));
      if (!groups.length) return;

      groups.forEach((group) => {
        const title = group.querySelector('.filter-group__title');
        if (!title) return;
        title.setAttribute('role', 'button');
        title.setAttribute('tabindex', '0');
      });
    });
  };

  const handleMobileFilterAccordionClick = (e) => {
    if (!C3D_MOBILE_MQ.matches) return;
    const title = e.target.closest('.filter-group__title');
    if (!title) return;
    const group = title.closest('[data-filter-group]');
    const filterGroups = title.closest('[data-filter-groups]');
    if (!group || !filterGroups) return;

    e.preventDefault();
    e.stopPropagation();

    const wasExpanded = group.classList.contains('is-expanded');
    filterGroups.querySelectorAll('[data-filter-group]').forEach((g) => {
      g.classList.remove('is-expanded');
    });
    if (!wasExpanded) group.classList.add('is-expanded');
    updateMobileFilterBackdrop();
  };

  const syncMobileFilterExpandedForPanel = (panel) => {
    if (!C3D_MOBILE_MQ.matches) return;
    document.querySelectorAll('[data-filter-group].is-expanded').forEach((group) => {
      group.classList.remove('is-expanded');
    });
    updateMobileFilterBackdrop();
  };

  const handleMobileFilterAccordionKeydown = (e) => {
    if (!C3D_MOBILE_MQ.matches || (e.key !== 'Enter' && e.key !== ' ')) return;
    const title = e.target.closest('.filter-group__title');
    if (!title) return;
    e.preventDefault();
    title.click();
  };

  const getC3dInteractionState = () =>
    document.querySelector('[data-3d-container]')?.__c3dState || null;

  const C3D_TOUCH_TOOLTIP_MQ = window.matchMedia('(hover: none), (pointer: coarse)');

  const setupPriceDisplayHintTooltips = () => {
    const useClickTooltip = () => C3D_TOUCH_TOOLTIP_MQ.matches;

    const closeAll = () => {
      document.querySelectorAll('.c3d-price-display-hint.is-tooltip-open').forEach((btn) => {
        btn.classList.remove('is-tooltip-open');
      });
    };

    document.querySelectorAll('.c3d-price-display-hint').forEach((btn) => {
      if (btn.dataset.c3dHintBound === '1') return;
      btn.dataset.c3dHintBound = '1';
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!useClickTooltip()) return;
        const willOpen = !btn.classList.contains('is-tooltip-open');
        closeAll();
        if (willOpen) btn.classList.add('is-tooltip-open');
      });
    });

    if (document.documentElement.dataset.c3dPriceHintDocBound === '1') return;
    document.documentElement.dataset.c3dPriceHintDocBound = '1';
    document.addEventListener('click', (e) => {
      if (e.target.closest('.c3d-price-display-hint')) return;
      closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });
  };

  const bindCustomization3dUiEvents = () => {
    if (document.documentElement.dataset.c3dUiEventsBound === '1') return;
    document.documentElement.dataset.c3dUiEventsBound = '1';

    setupPriceDisplayHintTooltips();

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.customization-3d-wrapper')) return;
      const state = getC3dInteractionState();
      if (!state) return;
      handlePrimaryClick(state)(e);
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.customization-3d-wrapper')) return;
      const state = getC3dInteractionState();
      if (!state) return;
      handleSecondaryClick(state)(e);
    }, true);

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.customization-3d-wrapper')) return;
      const state = getC3dInteractionState();
      if (!state) return;
      handleProductCardClick(state)(e);
    });
  };

  const init = () => {
    const wrapper = document.querySelector('.customization-3d-wrapper');
    if (!wrapper) return;

    const state = createState();
    const c3dHost = document.querySelector('[data-3d-container]');
    if (c3dHost) c3dHost.__c3dState = state;
    try {
      initC3dViewportLayoutSync();
      initMobileStickyHeadLayout();
      initMobileFilterAccordions();
      initMobileBottomBarDock();
      initMobileScrollToTop();
      updateMobileFilterBackdrop();
    } catch (err) {
      console.warn('[Customization] mobile filter accordion init failed:', err);
    }
    document.addEventListener('click', handleMobileFilterAccordionClick);
    document.addEventListener('keydown', handleMobileFilterAccordionKeydown);
    document.addEventListener('click', (e) => {
      if (!C3D_MOBILE_MQ.matches) return;
      if (e.target.closest('[data-c3d-filter-backdrop]')) {
        collapseMobileFilterAccordions();
      }
    });
    const onMobileFilterLayoutChange = () => updateMobileFilterBackdrop();
    window.addEventListener('resize', onMobileFilterLayoutChange);
    window.addEventListener('scroll', onMobileFilterLayoutChange, true);
    if (typeof C3D_MOBILE_MQ.addEventListener === 'function') {
      C3D_MOBILE_MQ.addEventListener('change', onMobileFilterLayoutChange);
    } else if (typeof C3D_MOBILE_MQ.addListener === 'function') {
      C3D_MOBILE_MQ.addListener(onMobileFilterLayoutChange);
    }
    updateHotspotFilterUi();
    setup3DEventBridge(state);
    setup3DContainerInteractionGuards();
    syncPrimary3dHostDataset(state);

    state.subscribe(() => {
      updatePergolaSizeLegFromState(state);
      updatePergolaProductLineFromState(state);
      refreshAllPanelFilters(state);
      updateTotal(state);
      updateBottomSummary(state);
      updatePreview(state);
      syncProductCards(state);
      syncPrimary3dHostDataset(state);
    });

    updatePergolaSizeLegFromState(state);
    updatePergolaProductLineFromState(state);
    document.querySelectorAll('.panel-content').forEach((panel) => {
      filterProducts(panel, state);
    });
    if (activeHotspotSkuFilter) applyHotspotSkuFilter([...activeHotspotSkuFilter]);

    bindCustomization3dUiEvents();
    document.querySelector('[data-clear-selection]')
      ?.addEventListener('click', handleClearAll(state));
    document.querySelector('[data-exit-3d]')?.addEventListener('click', () => {
      clearCustomizationSessionDraft();
      try {
        sessionStorage.removeItem(PDP_ADDONS_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      window.history.back();
    });

    window.addEventListener('pagehide', () => {
      clearCustomizationSessionDraft();
    });
    document.addEventListener('click', handlePreviewBubble(state));
    
    document.addEventListener('click', (e) => {
      if (
        pending3dPlacement
        && !e.target.closest('.product-card-clickable')
        && !e.target.closest('[data-preview-grid]')
        && !e.target.closest('[data-3d-container]')
      ) {
        void cancelPending3dPlacement();
      }
    });

    document.querySelectorAll('[data-add-all-cart]')
      .forEach(btn => btn.addEventListener('click', () => openTipsConfirmModal(state)));
    document.querySelectorAll('[data-cart-modal-close]')
      .forEach(btn => btn.addEventListener('click', closeCartModal));
    document.querySelector('[data-cart-modal-overlay]')
      ?.addEventListener('click', closeCartModal);
    document.querySelector('[data-cart-modal-add]')
      ?.addEventListener('click', handleModalViewCart(state));
    document.querySelector('[data-cart-modal-checkout]')
      ?.addEventListener('click', handleModalCheckout(state));

    initializeDefaultVariants(state);
    setTimeout(() => initializeDefaultVariants(state), 100);
    setupVariantListeners(state);
    preventDefaultClicks();

    warmCustomization3dEntry();
    setupLazyPanelPreload();

    if (!isPdpCustomizationEntry()) {
      clearCustomizationSessionDraft();
    }

    setTimeout(async () => {
      try {
        await applyPdpDeepLink(state);
        await applyPdpAddonsFromStorage(state);
      } catch (e) {
        console.warn('[Customization] PDP ? customization bridge failed:', e);
      }
    }, 200);

    document.addEventListener('click', (e) => {
      const selectAllWrapper = e.target.closest('[data-cart-select-all-wrapper]');
      if (selectAllWrapper) {
        const all = document.querySelectorAll('[data-cart-item-checkbox]');
        const isAllChecked = Array.from(all).every(cb => cb.checked);
        all.forEach(cb => { cb.checked = !isAllChecked; });
        updateSelectAllState();
        updateCartModalTotal(state);
      }

      const increaseBtn = e.target.closest('[data-cart-item-increase]');
      if (increaseBtn) {
        const productId = increaseBtn.dataset.cartItemIncrease;
        const input = document.querySelector(`[data-cart-item-quantity="${productId}"]`);
        if (input) {
          const qty = (parseInt(input.value) || 1) + 1;
          input.value = qty;
          const data = state.get().get(productId);
          if (data) data.quantity = qty;
          updateCartModalTotal(state);
        }
      }

      const decreaseBtn = e.target.closest('[data-cart-item-decrease]');
      if (decreaseBtn) {
        const productId = decreaseBtn.dataset.cartItemDecrease;
        const input = document.querySelector(`[data-cart-item-quantity="${productId}"]`);
        if (input) {
          const qty = parseInt(input.value) || 1;
          if (qty > 1) {
            input.value = qty - 1;
            const data = state.get().get(productId);
            if (data) data.quantity = qty - 1;
            updateCartModalTotal(state);
          }
        }
      }

      const deleteBtn = e.target.closest('[data-cart-item-delete]');
      if (deleteBtn) {
        const productId = deleteBtn.dataset.cartItemDelete;
        if (confirm('Sure to delete this product?')) {
          state.remove(productId);
          closeCartModal();
          if (state.get().size > 0) setTimeout(() => openCartModal(state), 100);
        }
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-item-checkbox]')) {
        updateSelectAllState();
        updateCartModalTotal(state);
      }
    });

    updateTotal(state);
    updateBottomSummary(state);
    updatePreview(state);
    syncProductCards(state);
  };

  const bootCustomization3d = () => {
    if (!document.querySelector('.customization-3d-wrapper')) return;
    init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCustomization3d);
  } else {
    bootCustomization3d();
  }
})();
